import React, { useState, useRef, useEffect } from "react";
import Text from "../../components/Atomsv2/Text/Text";
import Button from "../../components/Atomsv2/Button/Button";
import ActionButton from "../../components/Atomsv2/ActionButton/ActionButton";
import Input from "../../components/Atomsv2/Input/Input";
import Textarea from "../../components/Atomsv2/Textarea/Textarea";
import Select from "../../components/Atomsv2/Select/Select";
import MultiSelect from "../../components/Atomsv2/MultiSelect/MultiSelect";
import Calendar from "../../components/Atomsv2/Calendar/Calendar";
import Checkbox from "../../components/Atomsv2/Checkbox/Checkbox";
import Avatar from "../../components/Atomsv2/Avatar/Avatar";
import Status from "../../components/Atomsv2/Status/Status";
import { useToast, ToastContainer } from "../../components/Atomsv2/Toast/Toast";
import Tooltip from "../../components/Atomsv2/Tooltip/Tooltip";
import AnimationIcons from "../../components/Atomsv2/AnimationIcons/AnimationIcons";
import Modal from "../../components/Atomsv2/Modal/Modal";
import Confirm, { useConfirm } from "../../components/Atomsv2/Confirm/Confirm";
import EmptyState from "../../components/Atomsv2/EmptyState/EmptyState";
import Collapse from "../../components/moleculesv2/Collapse/Collapse";
import Steps from "../../components/moleculesv2/Steps/Steps";
import OptionsGroup from "../../components/moleculesv2/OptionsGroup/OptionsGroup";
import Sidebar from "../../components/moleculesv2/Sidebar/Sidebar";
import Table from "../../components/moleculesv2/Table/Table";
import Column from "../../components/moleculesv2/Table/Column";
import SidePanel from "../../components/organismsv2/SidePanel/SidePanel";
import SideMenu from "../../components/organismsv2/SideMenu/SideMenu";
import AuthGrid from "../../components/organismsv2/AuthGrid/AuthGrid";
import GanttChart from "../../components/organismsv2/GanttChart/GanttChart";
import SideDrawer from "../../components/moleculesv2/SideDrawer/SideDrawer";
import KanbanPanel from "../../components/moleculesv2/KanbanPanel/KanbanPanel";
import InfoTooltip from "../../components/molecules/InfoTooltip/InfoTooltip";
import DropdownButton from "../../components/moleculesv2/DropdownButton/DropdownButton";
import CardDashboard from "../../components/moleculesv2/DashboardCard/CardDashboard";
import DocsSidebar, { NAV } from "./DocsSidebar";
import "./Docs.scss";

// ─── Helpers de documentación ─────────────────────────────────────────────────

const PropsTable = ({ rows }) => (
    <div className="docs-table-wrap">
        <table className="docs-props-table">
            <thead>
                <tr>
                    <th>Prop</th>
                    <th>Tipo</th>
                    <th>Default</th>
                    <th>Descripción</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td><code className="prop-name">{r.name}</code></td>
                        <td><code className="prop-type">{r.type}</code></td>
                        <td><code className="prop-default">{r.default ?? "—"}</code></td>
                        <td className="prop-desc">{r.desc}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const Section = ({ id, title, badge, description, children }) => (
    <section id={id} className="docs-section">
        <div className="docs-section-header">
            <Text size="medium-large" weight={700} className="docs-title">{title}</Text>
            {badge && <span className={`docs-badge docs-badge--${badge}`}>{badge}</span>}
        </div>
        {description && (
            <Text color="var(--gray-dark-600)" className="docs-description">{description}</Text>
        )}
        {children}
    </section>
);

const SubSection = ({ title, children }) => (
    <div className="docs-subsection">
        <Text weight={600} size="medium" className="docs-subtitle">{title}</Text>
        {children}
    </div>
);

const Preview = ({ label, code, children }) => (
    <div className="docs-preview">
        {label && <div className="docs-preview-label">{label}</div>}
        <div className="docs-preview-content">{children}</div>
        {code && <pre className="docs-code"><code>{code.trim()}</code></pre>}
    </div>
);

const Grid = ({ children }) => (
    <div className="docs-preview-grid">{children}</div>
);

// ─── Demo interactivo de Sidebar ─────────────────────────────────────────────

const DEMO_NAV = [    {
        group: "Principal",
        items: [
            { id: "dashboard", label: "Dashboard", icon: "dashboard"  },
            { id: "reports",   label: "Reportes",  icon: "bar_chart"  },
            { id: "calendar",  label: "Calendario",icon: "calendar_month" },
        ],
    },
    {
        group: "Configuración",
        items: [
            { id: "settings", label: "Ajustes",  icon: "settings" },
            { id: "profile",  label: "Perfil",   icon: "person"   },
        ],
    },
];

const SidebarDemo = () => {
    const [active, setActive] = useState("dashboard");
    
    return (
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <div style={{ height: 260, display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--gray-300)" }}>
                <Sidebar
                    nav={DEMO_NAV}
                    activeId={active}
                    onSelect={setActive}
                    brand={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span className="icon" data-icon="widgets" style={{ color: "var(--blue-500)" }} />
                            <Text weight={700} size="medium">Mi App</Text>
                        </span>
                    }
                />
            </div>
            <Text size="small" color="var(--gray-dark-500)">
                Ítem activo: <strong>{active}</strong>
            </Text>
        </div>
    );
};

// ─── Demo GanttChart ──────────────────────────────────────────────────────────
const GANTT_ROWS = [
    { id: 'g1', label: 'Desarrollo',   icon: 'folder',  isGroup: true, indent: 0 },
    { id: 'g2', label: 'Sistema RRHH', icon: 'article', isGroup: true, indent: 1 },
    { id: 1, label: 'Gestión de usuarios', start: new Date(Date.now() - 10 * 86400000).toISOString().split('T')[0], end: new Date(Date.now() +  5 * 86400000).toISOString().split('T')[0], color: '#2563eb', badge: <Status severity="primary">En proceso</Status>,  icon: 'task_alt', indent: 2 },
    { id: 2, label: 'Módulo de reportes',  start: new Date(Date.now() +  6 * 86400000).toISOString().split('T')[0], end: new Date(Date.now() + 20 * 86400000).toISOString().split('T')[0], color: '#94a3b8', badge: <Status severity="warning">Sin empezar</Status>, icon: 'task_alt', indent: 2 },
    { id: 3, label: 'Configuración CI/CD', start: new Date(Date.now() -  2 * 86400000).toISOString().split('T')[0], end: new Date(Date.now() +  8 * 86400000).toISOString().split('T')[0], color: '#dc2626', badge: <Status severity="error">Cancelado</Status>,   icon: 'task_alt', indent: 2 },
    { id: 'g3', label: 'App Móvil',    icon: 'article', isGroup: true, indent: 1 },
    { id: 4, label: 'Diseño de interfaz', start: new Date(Date.now() - 20 * 86400000).toISOString().split('T')[0], end: new Date(Date.now() -  5 * 86400000).toISOString().split('T')[0], color: '#16a34a', badge: <Status severity="success">Terminado</Status>,  icon: 'task_alt', indent: 2 },
    { id: 5, label: 'Animaciones UI',     start: new Date(Date.now() -  4 * 86400000).toISOString().split('T')[0], end: new Date(Date.now() + 10 * 86400000).toISOString().split('T')[0], color: '#2563eb', badge: <Status severity="primary">En proceso</Status>,  icon: 'task_alt', indent: 2 },
];

const GanttChartDemo = () => {
    const [tarea, setTarea] = useState(null);
    return (
        <>
            <div style={{ height: 400, border: "1px solid var(--gray-200)", borderRadius: 8, overflow: "hidden" }}>
                <GanttChart rows={GANTT_ROWS} onClick={(row) => setTarea(row)} />
            </div>

            {tarea && (
                <Modal title={tarea.label} onClose={() => setTarea(null)} style={{ zIndex: 1300 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 280 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text size="small" color="var(--gray-dark-500)">Estado</Text>
                            {tarea.badge}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text size="small" color="var(--gray-dark-500)">Inicio</Text>
                            <Text size="small">{tarea.start}</Text>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Text size="small" color="var(--gray-dark-500)">Fin</Text>
                            <Text size="small">{tarea.end}</Text>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

// ─── Demo Table ───────────────────────────────────────────────────────────────
const TABLE_DATA = [
    { id: 1, nombre: "Ana García",    puesto: "Analista",    estado: "Activo",   activo: true  },
    { id: 2, nombre: "Luis Pérez",    puesto: "Supervisor",  estado: "Activo",   activo: true  },
    { id: 3, nombre: "María López",   puesto: "Coordinadora",estado: "Inactivo", activo: false },
    { id: 4, nombre: "Carlos Ruiz",   puesto: "Operador",    estado: "Activo",   activo: true  },
    { id: 5, nombre: "Sofía Martínez",puesto: "Analista",    estado: "Activo",   activo: true  },
    { id: 6, nombre: "Pedro Jiménez", puesto: "Supervisor",  estado: "Inactivo", activo: false },
];

const PUESTO_OPTIONS = [
    { label: "Analista",     value: "Analista"     },
    { label: "Supervisor",   value: "Supervisor"   },
    { label: "Coordinadora", value: "Coordinadora" },
    { label: "Operador",     value: "Operador"     },
];

const TableDemo = () => (
    <div style={{ height: 320 }}>
        <Table data={TABLE_DATA} stripedRows paginator rows={5} globalFilter height={280}>
            <Column header="ID"     value="id"     sortable width="60px" />
            <Column header="Nombre" value="nombre" sortable />
            <Column header="Puesto" value="puesto" sortable responsive />
            <Column header="Estado" value="estado"
                body={(row) => (
                    <span style={{
                        padding: "2px 10px",
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        background: row.estado === "Activo" ? "var(--green-100)" : "var(--gray-200)",
                        color: row.estado === "Activo" ? "var(--green-700)" : "var(--gray-dark-500)",
                    }}>
                        {row.estado}
                    </span>
                )}
            />
        </Table>
    </div>
);

const TableEditDemo = () => {
    const [rows, setRows] = useState(TABLE_DATA);
    const [lastSaved, setLastSaved] = useState(null);

    const handleRowEdit = (newRow, rowIndex) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? newRow : r));
        setLastSaved(newRow);
        
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ height: 320 }}>
                <Table 
                responsiveHideColumn data={rows} stripedRows  height={280} onRowEdit={handleRowEdit}>
                    <Column header="ID"     value="id"    width="60px" />
                    <Column header="Nombre" value="nombre" responsive edit="text"  width="180px" />
                    <Column
                        header="Puesto"
                        value="puesto"
                        responsive
                        width="160px"
                        edit="select"
                        editOptions={PUESTO_OPTIONS}
                        editCondition={(row) => row.activo}
                    />
                    <Column
                        header="Activo"
                        value="activo"
                        responsive
                        width="100px"
                        edit="checkbox"
                        body={(row) => (
                            <span style={{
                                padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                                background: row.activo ? "var(--green-100)" : "var(--gray-200)",
                                color: row.activo ? "var(--green-700)" : "var(--gray-dark-500)",
                            }}>
                                {row.activo ? "Sí" : "No"}
                            </span>
                        )}
                    />
                </Table>
            </div>
            {lastSaved && (
                <div className="docs-note" style={{ marginTop: 0 }}>
                    <span className="icon" data-icon="check_circle" />
                    <Text size="small">
                        Última fila guardada: <strong>{lastSaved.nombre}</strong> — Puesto: <strong>{lastSaved.puesto}</strong> — Activo: <strong>{lastSaved.activo ? "Sí" : "No"}</strong>
                    </Text>
                </div>
            )}
            <Text size="small" color="var(--gray-dark-500)">
                Nota: <em>Puesto</em> solo es editable cuando <em>Activo = Sí</em> (editCondition).
            </Text>
        </div>
    );
};

const TableColumnSelectDemo = () => (
    <div style={{ height: 320 }}>
        <Table
            data={TABLE_DATA}
            height={280}
            stripedRows
            columnSelect={["id", "nombre", "estado"]}
        >
            <Column header="ID"     value="id"     sortable width="60px" />
            <Column header="Nombre" value="nombre" sortable />
            <Column header="Puesto" value="puesto" sortable />
            <Column header="Estado" value="estado" />
            <Column header="Activo" value="activo" />
        </Table>
    </div>
);

const TableSkeletonDemo = () => {
    const [loading, setLoading] = useState(true);
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
                <Button onClick={() => setLoading(l => !l)} outline={!loading}>
                    {loading ? "Mostrar datos" : "Activar skeleton"}
                </Button>
            </div>
            <Table data={loading ? [] : TABLE_DATA} skeleton={loading} height={280} stripedRows>
                <Column header="ID"     value="id"     width="60px" />
                <Column header="Nombre" value="nombre" />
                <Column header="Puesto" value="puesto" />
                <Column header="Estado" value="estado" />
                <Column header="Activo" value="activo" />
            </Table>
        </div>
    );
};

// ─── Demo SidePanel ───────────────────────────────────────────────────────────
const SidePanelDemo = () => {
    const [open, setOpen]           = useState(false);
    const [placement, setPlacement] = useState("right");
    return (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <Button onClick={() => { setPlacement("right"); setOpen(true); }}>
                Abrir derecha
            </Button>
            <Button variant="secondary" onClick={() => { setPlacement("left"); setOpen(true); }}>
                Abrir izquierda
            </Button>
            <SidePanel
                open={open}
                onClose={() => setOpen(false)}
                title="Panel de ejemplo"
                subtitle="Guía de uso"
                icon="info"
                placement={placement}
                footer={
                    <Button onClick={() => setOpen(false)}>Cerrar</Button>
                }
            >
                <Text color="var(--text-secondary)">
                    Este es el contenido del panel. Puede incluir cualquier elemento React:
                    formularios, tablas, listas, etc.
                </Text>
            </SidePanel>
        </div>
    );
};

// ─── KanbanPanel — ejemplo completo: gestión de solicitudes de mantenimiento ──

const KANBAN_MANTENIMIENTO_DEFAULT = [
    {
        id: "por-asignar",
        title: "Por asignar",
        items: [
            { id: "sol-001", title: "Cambio de aceite U-29",      description: "HIACE · venc. hoy",            tag: "Urgente" },
            { id: "sol-002", title: "Revision de frenos U-50",    description: "YARIS · programado",           tag: "Normal"  },
            { id: "sol-003", title: "Calibracion de luces U-76",  description: "SPRINTER · pendiente verificar"              },
        ],
    },
    {
        id: "en-revision",
        title: "En revision",
        items: [
            { id: "sol-004", title: "Cambio de llanta U-55",      description: "SPRINTER · taller norte",      tag: "Normal"  },
        ],
    },
    {
        id: "en-ejecucion",
        title: "En ejecucion",
        items: [
            { id: "sol-005", title: "Motor U-98",                  description: "ORION VW · taller largo plazo", tag: "Urgente" },
        ],
    },
    {
        id: "finalizado",
        title: "Finalizado",
        items: [
            { id: "sol-006", title: "Cambio de bateria U-32",     description: "HIACE · completado 11/07",     tag: "OK"      },
        ],
    },
];

const COLOR_POR_COLUMNA = {
    "por-asignar":  "var(--gray-dark-500)",
    "en-revision":  "var(--orange-600)",
    "en-ejecucion": "var(--blue-600)",
    "finalizado":   "var(--green-600)",
};

const KanbanPanelFullDemo = () => {
    const [modules, setModules] = useState(KANBAN_MANTENIMIENTO_DEFAULT);

    return (
        <KanbanPanel
            title="Solicitudes de mantenimiento"
            modules={modules}
            onChange={(nextModules) => setModules(nextModules)}
            columnHeight={280}
            emptyMessage="Sin solicitudes — arrastra una tarjeta aqui"
            showHistory
            showReset
            maxHistory={5}
        />
    );
};

// ─── Página de documentación ──────────────────────────────────────────────────

const Docs = () => {
    const contentRef = useRef(null);
    const [activeId, setActiveId] = useState("text");

    // — Estado para ejemplos interactivos —
    const [btnLoading, setBtnLoading]       = useState(false);
    const [inputVal, setInputVal]           = useState("");
    const [currencyVal, setCurrencyVal]     = useState("$ 0.00");
    const [textareaVal, setTextareaVal]     = useState("");
    const [selectVal, setSelectVal]         = useState(null);
    const [multiVal, setMultiVal]           = useState([]);
    const [calDate, setCalDate]             = useState("");
    const [calDateTime, setCalDateTime]     = useState("");
    const [checkA, setCheckA]               = useState(false);
    const [checkB, setCheckB]               = useState(true);
    const [modalOpen, setModalOpen]         = useState(false);
    const [confirmOpen, setConfirmOpen]     = useState(false);
    const { confirm, ConfirmDialog }        = useConfirm();
    const [stepsActive, setStepsActive]     = useState(2);
    const [optionsVal, setOptionsVal]       = useState("week");
    const { toasts, addToast, removeToast } = useToast();

    const countryOptions = [
        { name: "México",    value: "mx" },
        { name: "Colombia",  value: "co" },
        { name: "Argentina", value: "ar" },
        { name: "Chile",     value: "cl" },
        { name: "Perú",      value: "pe" },
    ];

    const stepsData = [
        { label: "Configurar cuenta",    description: "Ingresa tus datos básicos de usuario.",   icon: "manage_accounts" },
        { label: "Verificar identidad",  description: "Confirma tu correo electrónico.",          icon: "verified_user"   },
        { label: "Personalizar perfil",  description: "Agrega foto y preferencias.",              icon: "person"          },
        { label: "Completar",            description: "Tu cuenta está lista para usarse.",        icon: "check_circle"    },
    ];

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el && contentRef.current) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        
        setActiveId(id);
    };

    // Resalta el ítem de navegación según la sección visible
    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;
        const handleScroll = () => {
            const sections = NAV.flatMap(g => g.items);
            for (let i = sections.length - 1; i >= 0; i--) {
                const el = document.getElementById(sections[i].id);
                if (el && el.offsetTop - 80 <= container.scrollTop) {
                    setActiveId(sections[i].id);
                    break;
                }
            }
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    
    return (
        <div className="Docs">
            
            {/* ── Sidebar ── */}
            <DocsSidebar activeId={activeId} onSelect={scrollTo} className="docs-sidebar" />

            {/* ── Contenido principal ── */}
            <main className="docs-content" ref={contentRef}>

                <div className="docs-page-header">
                    <Text size="extra-large" weight={800}>Librería de componentes</Text>
                    <Text color="var(--gray-dark-600)" size="medium" className="docs-page-subtitle">
                        Guía de referencia completa: props, comportamiento y ejemplos interactivos de cada componente.
                    </Text>
                </div>

                {/* ═══════════════════════════════ TEXT ═══════════════════════════════ */}
                <Section id="text" badge="atom" title="Text"
                    description="Componente base para renderizar texto con control de tamaño tipográfico, peso, color y altura de línea. Se renderiza como un div inline-block.">

                    <PropsTable rows={[
                        { name: "children",    type: "ReactNode", desc: "Define el texto o elementos que se muestran dentro del componente." },
                        { name: "size",        type: "string",    default: '"paragraph"', desc: "Controla el tamaño de fuente. Elige entre: extra-small · small · paragraph · medium · medium-large · title · extra-large." },
                        { name: "weight",      type: "number",    default: "400",          desc: "Controla el grosor de la fuente. Valores de 100 (ultra delgado) a 900 (negro)." },
                        { name: "color",       type: "string",    default: "var(--font-color)", desc: "Cambia el color del texto. Acepta variables CSS del sistema de diseño, hex o cualquier valor CSS válido." },
                        { name: "lineHeight",  type: "string|number", desc: "Ajusta el espaciado vertical entre líneas. Útil para textos multilinea donde el interlineado importa." },
                        { name: "className",   type: "string",    default: '""',           desc: "Agrega clases CSS extra al elemento, sin reemplazar las existentes." },
                        { name: "...rest",     type: "any",       desc: "Cualquier atributo HTML válido (id, style, onClick, etc.) se pasa directamente al div contenedor." },
                    ]} />

                    <SubSection title="Tamaños (size)">
                        <Preview
                            label="extra-small · small · paragraph · medium · medium-large · title · extra-large"
                            code={`<Text size="extra-small">Extra small</Text>
<Text size="small">Small</Text>
<Text size="paragraph">Paragraph (default)</Text>
<Text size="medium">Medium</Text>
<Text size="medium-large">Medium Large</Text>
<Text size="title">Title</Text>
<Text size="extra-large">Extra Large</Text>`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <Text size="extra-small">Extra small</Text>
                                <Text size="small">Small</Text>
                                <Text size="paragraph">Paragraph (default)</Text>
                                <Text size="medium">Medium</Text>
                                <Text size="medium-large">Medium Large</Text>
                                <Text size="title">Title</Text>
                                <Text size="extra-large">Extra Large</Text>
                            </div>
                        </Preview>
                    </SubSection>

                    <SubSection title="Pesos (weight)">
                        <Preview
                            label="400 Regular · 600 SemiBold · 700 Bold · 800 ExtraBold"
                            code={`<Text weight={400}>Regular — 400</Text>
<Text weight={600}>SemiBold — 600</Text>
<Text weight={700}>Bold — 700</Text>
<Text weight={800}>ExtraBold — 800</Text>`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <Text weight={400}>Regular — 400</Text>
                                <Text weight={600}>SemiBold — 600</Text>
                                <Text weight={700}>Bold — 700</Text>
                                <Text weight={800}>ExtraBold — 800</Text>
                            </div>
                        </Preview>
                    </SubSection>

                    <SubSection title="Color">
                        <Preview
                            label="Usando variables CSS del sistema de diseño"
                            code={`<Text color="var(--blue-600)">Color primario</Text>
<Text color="var(--green-600)">Color éxito</Text>
<Text color="var(--red-600)">Color error</Text>
<Text color="var(--orange-600)">Color advertencia</Text>
<Text color="var(--gray-dark-500)">Color secundario</Text>`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <Text color="var(--blue-600)">Color primario</Text>
                                <Text color="var(--green-600)">Color éxito</Text>
                                <Text color="var(--red-600)">Color error</Text>
                                <Text color="var(--orange-600)">Color advertencia</Text>
                                <Text color="var(--gray-dark-500)">Color secundario</Text>
                            </div>
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ BUTTON ═══════════════════════════════ */}
                <Section id="button" badge="atom" title="Button"
                    description="Botón con soporte de íconos, estado de carga animado, variante outline y sistema de severidades semánticas. Se envuelve en un div para aplicar width.">

                    <PropsTable rows={[
                        { name: "children",   type: "ReactNode", desc: "Texto o elementos que se muestran como etiqueta del botón." },
                        { name: "onClick",    type: "function",  desc: "Se ejecuta cuando el usuario hace clic. No se llama si el botón está disabled o en estado loading." },
                        { name: "icon",       type: "string",    desc: "Muestra un ícono de Material Icons a la izquierda del texto. Se oculta automáticamente mientras loading=true para dar espacio al spinner." },
                        { name: "type",       type: "string",    default: '"button"', desc: "Determina el comportamiento del botón en formularios. submit envía el form, reset lo limpia, button no tiene efecto nativo." },
                        { name: "width",      type: "string",    default: '"fit-content"', desc: 'Controla el ancho del div que envuelve el botón. Usa "fit-content" para ajustarse al texto, o "100%" para ocupar todo el espacio disponible.' },
                        { name: "raised",     type: "bool",      default: "false", desc: "Añade una sombra para dar sensación de elevación. Útil para botones de acción principal que deben destacar visualmente." },
                        { name: "loading",    type: "bool",      default: "false", desc: "Reemplaza el contenido por un spinner animado y deshabilita el botón automáticamente, evitando envíos duplicados mientras una operación está en curso." },
                        { name: "disabled",   type: "bool",      default: "false", desc: "Bloquea el botón, impide que responda a clics y aplica estilos visuales de inactivo." },
                        { name: "outline",    type: "bool",      default: "false", desc: "Cambia la apariencia a solo borde y texto sin relleno. Ideal para acciones secundarias junto a un botón primario." },
                        { name: "severity",   type: "string",    desc: "Aplica un color semántico al botón. success = verde, error = rojo, warning = naranja. Funciona tanto en modo sólido como outline." },
                        { name: "className",  type: "string",    default: '""', desc: "Agrega clases CSS extra al botón, sin reemplazar las existentes." },
                        { name: "style",      type: "object",    desc: "Aplica estilos en línea directamente sobre el div contenedor del botón." },
                    ]} />

                    <SubSection title="Variantes base">
                        <Preview
                            label="Default · Raised (con sombra) · Con ícono"
                            code={`<Button>Default</Button>
<Button raised>Raised</Button>
<Button icon="save">Con ícono</Button>
<Button icon="add" raised>Raised con ícono</Button>`}>
                            <Button>Default</Button>
                            <Button raised>Raised</Button>
                            <Button icon="save">Con ícono</Button>
                            <Button icon="add" raised>Raised con ícono</Button>
                        </Preview>
                    </SubSection>

                    <SubSection title="Severidades (severity)">
                        <Preview
                            label="Relleno: success · error · warning"
                            code={`<Button severity="success">Success</Button>
<Button severity="error">Error</Button>
<Button severity="warning">Warning</Button>`}>
                            <Button severity="success">Success</Button>
                            <Button severity="error">Error</Button>
                            <Button severity="warning">Warning</Button>
                        </Preview>
                        <Preview
                            label="Outline: success · error · warning"
                            code={`<Button outline severity="success">Success</Button>
<Button outline severity="error">Error</Button>
<Button outline severity="warning">Warning</Button>`}>
                            <Button outline severity="success">Success</Button>
                            <Button outline severity="error">Error</Button>
                            <Button outline severity="warning">Warning</Button>
                        </Preview>
                    </SubSection>

                    <SubSection title="Estados">
                        <Preview
                            label="Loading (click para activar 2 seg) · Disabled"
                            code={`// loading deshabilita el botón automáticamente
<Button loading={isLoading} onClick={() => setIsLoading(true)}>
  Guardar
</Button>
<Button disabled>Deshabilitado</Button>`}>
                            <Button
                                loading={btnLoading}
                                onClick={() => { setBtnLoading(true); setTimeout(() => setBtnLoading(false), 2000); }}
                            >
                                {btnLoading ? "Procesando..." : "Click para cargar"}
                            </Button>
                            <Button disabled>Deshabilitado</Button>
                            <Button outline disabled>Outline disabled</Button>
                        </Preview>
                    </SubSection>

                    <SubSection title="Ancho (width)">
                        <Preview
                            label='width="100%" · width="200px"'
                            code={`<Button width="100%">Botón de ancho completo</Button>
<Button width="200px">200px fijo</Button>`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                                <Button width="100%">Botón de ancho completo</Button>
                                <Button width="200px">200px fijo</Button>
                            </div>
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ ACTION BUTTON ═══════════════════════════════ */}
                <Section id="action-button" badge="atom" title="ActionButton"
                    description="Botón icónico circular para acciones contextuales. Muestra un Tooltip automático cuando se proporciona title. Soporta severidades de hover y variante outline.">

                    <PropsTable rows={[
                        { name: "icon",      type: "string",   desc: "Nombre del ícono de Material Icons que se renderiza. Es el único contenido visible del botón, por lo que es obligatorio." },
                        { name: "title",     type: "string",   desc: "Activa un Tooltip automático con este texto al pasar el cursor. Si no se pasa, el botón no muestra ningún tooltip." },
                        { name: "onClick",   type: "function", desc: "Se ejecuta al hacer clic. No se dispara si el botón está en estado disabled." },
                        { name: "disabled",  type: "bool",     default: "false", desc: "Bloquea el botón visualmente y evita que responda a clics." },
                        { name: "color",     type: "string",   desc: "Sobreescribe el color del ícono en estado normal, sin usar severity. Útil para íconos con significado de color específico." },
                        { name: "severity",  type: "string",   desc: "Define el color que toma el ícono al pasar el cursor. Comunica la consecuencia de la acción: success = aprobar, error = eliminar, warning = advertir." },
                        { name: "outline",   type: "bool",     default: "false", desc: "Agrega un borde permanente al botón y hace el cambio de color al hover más pronunciado. Útil cuando el botón necesita más visibilidad contextual." },
                    ]} />

                    <SubSection title="Default y con Tooltip">
                        <Preview
                            label="Sin title · Con title (hover para ver el tooltip)"
                            code={`// Sin tooltip
<ActionButton icon="edit" />

// Con tooltip automático
<ActionButton icon="delete" title="Eliminar registro" />
<ActionButton icon="download" title="Descargar archivo" />
<ActionButton icon="visibility" title="Ver detalle" />`}>
                            <ActionButton icon="edit" />
                            <ActionButton icon="delete" title="Eliminar registro" />
                            <ActionButton icon="download" title="Descargar archivo" />
                            <ActionButton icon="visibility" title="Ver detalle" />
                        </Preview>
                    </SubSection>

                    <SubSection title="Severidades (color al hover)">
                        <Preview
                            label="success · error · warning — el color se activa al hacer hover"
                            code={`<ActionButton icon="check_circle" severity="success" title="Aprobar" />
<ActionButton icon="delete"      severity="error"   title="Eliminar" />
<ActionButton icon="warning"     severity="warning" title="Advertencia" />`}>
                            <ActionButton icon="check_circle" severity="success" title="Aprobar" />
                            <ActionButton icon="delete"       severity="error"   title="Eliminar" />
                            <ActionButton icon="warning"      severity="warning" title="Advertencia" />
                        </Preview>
                    </SubSection>

                    <SubSection title="Variante Outline">
                        <Preview
                            label="outline · outline + severity"
                            code={`<ActionButton icon="edit"         outline title="Editar" />
<ActionButton icon="check_circle" outline severity="success" title="Aprobar" />
<ActionButton icon="delete"       outline severity="error"   title="Eliminar" />
<ActionButton icon="warning"      outline severity="warning" title="Advertencia" />`}>
                            <ActionButton icon="edit"         outline title="Editar" />
                            <ActionButton icon="check_circle" outline severity="success" title="Aprobar" />
                            <ActionButton icon="delete"       outline severity="error"   title="Eliminar" />
                            <ActionButton icon="warning"      outline severity="warning" title="Advertencia" />
                        </Preview>
                    </SubSection>

                    <SubSection title="Disabled y color override">
                        <Preview
                            label='disabled · color="#e91e63" (override directo)'
                            code={`<ActionButton icon="block"    disabled title="No disponible" />
<ActionButton icon="favorite" color="#e91e63" title="Me gusta" />
<ActionButton icon="star"     color="var(--orange-500)" title="Favorito" />`}>
                            <ActionButton icon="block"    disabled title="No disponible" />
                            <ActionButton icon="favorite" color="#e91e63" title="Me gusta" />
                            <ActionButton icon="star"     color="var(--orange-500)" title="Favorito" />
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ INPUT ═══════════════════════════════ */}
                <Section id="input" badge="atom" title="Input"
                    description="Campo de texto controlado con soporte de ícono a la izquierda, tipo password con toggle de visibilidad, label y estado de error con mensaje.">

                    <PropsTable rows={[
                        { name: "label",        type: "string",   desc: "Texto que aparece encima del campo para indicarle al usuario qué dato debe ingresar." },
                        { name: "type",         type: "string",   default: '"text"', desc: 'Tipo de datos que acepta el campo. Usando "password" activa automáticamente un botón para mostrar u ocultar el texto ingresado. Con "currency" solo acepta números y formatea automáticamente como moneda ($ 0.00).' },
                        { name: "placeholder",  type: "string",   desc: "Texto de ejemplo que aparece cuando el campo está vacío. Desaparece en cuanto el usuario empieza a escribir." },
                        { name: "value",        type: "string",   desc: "Controla el texto mostrado en el campo desde el componente padre. Siémpre úsalo junto con onChange para mantener el estado sincronizado." },
                        { name: "onChange",     type: "function", desc: "Se ejecuta cada vez que el usuario escribe. Recibe el evento nativo; usa e.target.value para obtener el texto actual." },
                        { name: "icon",         type: "string",   desc: "Muestra un ícono de Material Icons dentro del campo, alineado a la izquierda. Útil para reforzar visualmente el tipo de dato (ej: search, email, phone)." },
                        { name: "name",         type: "string",   desc: "Identifica el campo dentro de un formulario HTML nativo. Necesario cuando el form se envía sin estado de React." },
                        { name: "error",        type: "bool",     default: "false", desc: "Cambia el borde del campo a rojo y hace visible el mensaje de error bajo el campo. Actívalo cuando la validación falla." },
                        { name: "errorMessage", type: "string",   default: '"error"', desc: "Mensaje que aparece debajo del campo cuando error=true. Indica al usuario qué debe corregir." },
                        { name: "disabled",     type: "bool",     default: "false", desc: "Bloquea el campo visualmente e impide que el usuario edite su contenido." },
                        { name: "className",    type: "string",   default: '""', desc: "Agrega clases CSS extra al contenedor del campo, sin reemplazar las existentes." },
                    ]} />

                    <Grid>
                        <Preview label="Default con label"
                            code={`<Input
  label="Nombre completo"
  placeholder="Ej. Juan Pérez"
  value={val}
  onChange={e => setVal(e.target.value)}
/>`}>
                            <Input label="Nombre completo" placeholder="Ej. Juan Pérez" value={inputVal} onChange={e => setInputVal(e.target.value)} />
                        </Preview>

                        <Preview label="Con ícono (icon)"
                            code={`<Input
  label="Buscar"
  icon="search"
  placeholder="Escribe para buscar..."
  value={val}
  onChange={e => setVal(e.target.value)}
/>`}>
                            <Input label="Buscar" icon="search" placeholder="Escribe para buscar..." value={inputVal} onChange={e => setInputVal(e.target.value)} />
                        </Preview>

                        <Preview label='type="password" — toggle de visibilidad'
                            code={`<Input
  label="Contraseña"
  type="password"
  placeholder="••••••••"
  value={val}
  onChange={e => setVal(e.target.value)}
/>`}>
                            <Input label="Contraseña" type="password" placeholder="••••••••" value={inputVal} onChange={e => setInputVal(e.target.value)} />
                        </Preview>

                        <Preview label='type="currency" — solo números y formato moneda'
                            code={`<Input
  label="Monto"
  type="currency"
  value={currencyVal}
  onChange={e => setCurrencyVal(e.target.value)}
/>`}>
                            <Input label="Monto" type="currency" value={currencyVal} onChange={e => setCurrencyVal(e.target.value)} />
                        </Preview>

                        <Preview label="Estado de error (error + errorMessage)"
                            code={`<Input
  label="Correo electrónico"
  placeholder="correo@ejemplo.com"
  value=""
  error
  errorMessage="El correo es obligatorio"
/>`}>
                            <Input label="Correo electrónico" placeholder="correo@ejemplo.com" value="" onChange={() => {}} error errorMessage="El correo es obligatorio" />
                        </Preview>

                        <Preview label="Disabled"
                            code={`<Input
  label="Campo de solo lectura"
  value="Valor no editable"
  disabled
/>`}>
                            <Input label="Campo de solo lectura" value="Valor no editable" onChange={() => {}} disabled />
                        </Preview>
                    </Grid>
                </Section>

                {/* ═══════════════════════════════ TEXTAREA ═══════════════════════════════ */}
                <Section id="textarea" badge="atom" title="Textarea"
                    description="Área de texto multilínea con la misma apariencia visual que Input. Permite controlar filas visibles y comportamiento de redimensionado.">

                    <PropsTable rows={[
                        { name: "label",        type: "string",   desc: "Texto que aparece encima del campo para indicarle al usuario qué dato debe ingresar." },
                        { name: "placeholder",  type: "string",   desc: "Texto de ejemplo que aparece cuando el campo está vacío. Desaparece al empezar a escribir." },
                        { name: "value",        type: "string",   desc: "Controla el texto mostrado en el campo desde el componente padre. Siémpre úsalo junto con onChange para mantener el estado sincronizado." },
                        { name: "onChange",     type: "function", desc: "Se ejecuta cada vez que el usuario escribe. Recibe el evento nativo; usa e.target.value para obtener el texto actual." },
                        { name: "name",         type: "string",   desc: "Identifica el campo dentro de un formulario HTML nativo. Necesario cuando el form se envía sin estado de React." },
                        { name: "rows",         type: "number",   default: "4", desc: "Define la altura inicial del campo en número de líneas de texto visibles. El usuario puede redimensionarlo según lo permita resize." },
                        { name: "resize",       type: "string",   default: '"vertical"', desc: "Controla si el usuario puede cambiar el tamaño del campo: vertical (solo alto), horizontal (solo ancho), both (ambos), none (fijo sin redimensionar)." },
                        { name: "error",        type: "bool",     default: "false", desc: "Cambia el borde a rojo y hace visible el mensaje de error. Actívalo cuando la validación falla." },
                        { name: "errorMessage", type: "string",   default: '"Campo requerido"', desc: "Mensaje que aparece debajo del campo cuando error=true. Indica al usuario qué debe corregir." },
                        { name: "disabled",     type: "bool",     default: "false", desc: "Bloquea el campo visualmente e impide que el usuario edite su contenido." },
                        { name: "className",    type: "string",   default: '""', desc: "Agrega clases CSS extra al contenedor del campo, sin reemplazar las existentes." },
                    ]} />

                    <Grid>
                        <Preview label="Default — 4 filas, resize vertical"
                            code={`<Textarea
  label="Comentarios"
  placeholder="Escribe aquí..."
  rows={4}
  value={val}
  onChange={e => setVal(e.target.value)}
/>`}>
                            <Textarea label="Comentarios" placeholder="Escribe aquí..." rows={4} value={textareaVal} onChange={e => setTextareaVal(e.target.value)} />
                        </Preview>

                        <Preview label='resize="none" — sin redimensionado'
                            code={`<Textarea
  label="Descripción corta"
  rows={3}
  resize="none"
  value={val}
  onChange={e => setVal(e.target.value)}
/>`}>
                            <Textarea label="Descripción corta" rows={3} resize="none" value={textareaVal} onChange={e => setTextareaVal(e.target.value)} />
                        </Preview>

                        <Preview label="Estado de error"
                            code={`<Textarea
  label="Motivo"
  value=""
  error
  errorMessage="El motivo es obligatorio"
/>`}>
                            <Textarea label="Motivo" value="" onChange={() => {}} error errorMessage="El motivo es obligatorio" />
                        </Preview>

                        <Preview label="Disabled"
                            code={`<Textarea
  label="Notas del sistema"
  value="Solo lectura — generado automáticamente."
  disabled
/>`}>
                            <Textarea label="Notas del sistema" value="Solo lectura — generado automáticamente." onChange={() => {}} disabled />
                        </Preview>
                    </Grid>
                </Section>

                {/* ═══════════════════════════════ SELECT ═══════════════════════════════ */}
                <Section id="select" badge="atom" title="Select"
                    description="Dropdown de selección única con búsqueda integrada. Recibe un array de opciones {name, value} y devuelve el value de la opción seleccionada.">

                    <PropsTable rows={[
                        { name: "label",        type: "string",   desc: "Texto que aparece encima del campo para identificar qué se debe seleccionar." },
                        { name: "value",        type: "any",      desc: "Opción actualmente seleccionada. Debe coincidir con el value de una de las opciones. Pasa null para limpiar la selección." },
                        { name: "onChange",     type: "function", desc: "Se ejecuta al elegir una opción. Recibe el objeto completo seleccionado ({ name, value }) para que puedas actualizar tu estado." },
                        { name: "options",      type: "array",    desc: "Array de objetos que alimenta el menú. Cada objeto debe tener name (texto visible) y value (identificador que se emite en onChange)." },
                        { name: "placeholder",  type: "string",   default: '"Seleccionar..."', desc: "Texto que aparece en el campo cuando aún no se ha elegido ninguna opción." },
                        { name: "icon",         type: "string",   desc: "Muestra un ícono de Material Icons dentro del campo, a la izquierda. Ayuda a identificar visualmente el tipo de selección." },
                        { name: "searchable",   type: "bool",     default: "true", desc: "Agrega un campo de texto dentro del menú que filtra las opciones en tiempo real al escribir. Desactívalo solo si hay pocas opciones." },
                        { name: "disabled",     type: "bool",     default: "false", desc: "Bloquea el dropdown visualmente e impide que el usuario lo abra o cambie la selección." },
                        { name: "error",        type: "bool",     default: "false", desc: "Cambia el borde a rojo y hace visible el mensaje de error. Actívalo cuando la validación falla." },
                        { name: "errorMessage", type: "string",   default: '"Campo requerido"', desc: "Mensaje que aparece debajo del campo cuando error=true." },
                        { name: "body",         type: "function", default: "null", desc: "(data) => ReactNode. Render personalizado para cada opción y para el valor seleccionado. data es el objeto normalizado de la opción con todos sus campos originales." },
                    ]} />

                    <Grid>
                        <Preview label="Default con búsqueda"
                            code={`<Select
  label="País"
  value={val}
  onChange={setVal}
  options={[
    { name: "México",    value: "mx" },
    { name: "Colombia",  value: "co" },
    // ...
  ]}
/>`}>
                            <Select label="País" value={selectVal} onChange={setSelectVal} options={countryOptions} />
                        </Preview>

                        <Preview label="Con ícono + sin búsqueda (searchable=false)"
                            code={`<Select
  label="Región"
  icon="public"
  searchable={false}
  value={val}
  onChange={setVal}
  options={opts}
/>`}>
                            <Select label="Región" icon="public" searchable={false} value={selectVal} onChange={setSelectVal} options={countryOptions} />
                        </Preview>

                        <Preview label="Estado de error"
                            code={`<Select
  label="País de origen"
  value={null}
  error
  errorMessage="Selecciona un país"
  options={opts}
/>`}>
                            <Select label="País de origen" value={null} onChange={() => {}} error errorMessage="Selecciona un país" options={countryOptions} />
                        </Preview>

                        <Preview label="Disabled"
                            code={`<Select
  label="País (bloqueado)"
  value="mx"
  disabled
  options={opts}
/>`}>
                            <Select label="País (bloqueado)" value="mx" onChange={() => {}} disabled options={countryOptions} />
                        </Preview>
                    </Grid>

                    <Preview label="Estado como objeto — guardar solo parte del resultado" code={`// onChange entrega el objeto completo de la opción seleccionada.
// Si tu estado guarda solo { label, value }, extrae los campos que necesitas.

const [form, setForm] = useState({
  area: '',
  responsable: null,  // { label: string, value: any } | null
});

<Select
  label="Responsable"
  icon="account_circle"
  options={usuarios}              // [{ name: "Ana", value: 1 }, ...]
  value={form.responsable?.value} // pasa el primitivo al Select, no el objeto
  onChange={(opt) =>
    setForm(prev => ({
      ...prev,
      responsable: opt
        ? { label: opt.name ?? opt.label, value: opt.value }
        : null,          // cleaner → limpia el campo
    }))
  }
/>

// Al enviar el formulario:
// form.responsable?.value  → ID para la BD
// form.responsable?.label  → nombre para mostrar`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>

                    <Preview label="body — render personalizado de opciones" code={`// body recibe el objeto normalizado de la opción.
// Puedes acceder a cualquier campo extra que hayas incluido en options.

const statusOptions = [
  { name: "Activo",    value: "active",   dot: "#22c55e" },
  { name: "Pausado",   value: "paused",   dot: "#f59e0b" },
  { name: "Inactivo",  value: "inactive", dot: "#ef4444" },
];

<Select
  label="Estado"
  options={statusOptions}
  value={val}
  onChange={setVal}
  body={(data) => (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%",
                     background: data.dot, flexShrink: 0 }} />
      {data.name}
    </span>
  )}
/>`}>
                        <Select
                            label="Estado"
                            options={[
                                { name: "Activo",   value: "active",   dot: "#22c55e" },
                                { name: "Pausado",  value: "paused",   dot: "#f59e0b" },
                                { name: "Inactivo", value: "inactive", dot: "#ef4444" },
                            ]}
                            value={selectVal}
                            onChange={setSelectVal}
                            body={(data) => (
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: data.dot, flexShrink: 0 }} />
                                    {data.name}
                                </span>
                            )}
                        />
                    </Preview>
                </Section>
                <Section id="multiselect" badge="atom" title="MultiSelect"
                    description='Dropdown de selección múltiple mediante checkboxes. Devuelve un array de objetos { name, value }. Incluye fila "Seleccionar todo" con estado indeterminado cuando la selección es parcial.'>

                    <PropsTable rows={[
                        { name: "label",        type: "string",   desc: "Texto que aparece encima del campo para identificar qué se está seleccionando." },
                        { name: "value",        type: "array",    desc: "Array que contiene los objetos actualmente seleccionados ({ name, value }). Mantenlo en estado con useState y pasa su setter a onChange." },
                        { name: "onChange",     type: "function", desc: "Se ejecuta cada vez que cambia la selección. Recibe el array completo y actualizado con todos los objetos seleccionados." },
                        { name: "options",      type: "array",    desc: "Array de objetos que alimenta el menú. Cada objeto debe tener name (texto visible) y value (identificador)." },
                        { name: "placeholder",  type: "string",   default: '"Seleccionar..."', desc: "Texto visible cuando no hay nada seleccionado. Con selecciones activas muestra hasta 2 nombres, o 'N seleccionados' si hay más." },
                        { name: "icon",         type: "string",   desc: "Muestra un ícono de Material Icons dentro del campo, a la izquierda." },
                        { name: "searchable",   type: "bool",     default: "true", desc: "Agrega un campo de texto que filtra las opciones en tiempo real. El botón 'Seleccionar todo' aplica solo sobre las opciones visibles filtradas." },
                        { name: "disabled",     type: "bool",     default: "false", desc: "Bloquea el dropdown visualmente e impide que el usuario lo abra o modifique la selección." },
                        { name: "error",        type: "bool",     default: "false", desc: "Cambia el borde a rojo y hace visible el mensaje de error." },
                        { name: "errorMessage", type: "string",   default: '"Campo requerido"', desc: "Mensaje que aparece debajo del campo cuando error=true." },
                    ]} />

                    <Grid>
                        <Preview label="Default — selección múltiple con búsqueda"
                            code={`const [sel, setSel] = useState([]);

<MultiSelect
  label="Países"
  value={sel}
  onChange={setSel}
  options={[
    { name: "México",   value: "mx" },
    { name: "Colombia", value: "co" },
    // ...
  ]}
/>`}>
                            <MultiSelect label="Países" value={multiVal} onChange={setMultiVal} options={countryOptions} />
                        </Preview>

                        <Preview label="Con ícono + sin búsqueda"
                            code={`<MultiSelect
  label="Regiones"
  icon="public"
  searchable={false}
  value={sel}
  onChange={setSel}
  options={opts}
/>`}>
                            <MultiSelect label="Regiones" icon="public" searchable={false} value={multiVal} onChange={setMultiVal} options={countryOptions} />
                        </Preview>

                        <Preview label="Estado de error"
                            code={`<MultiSelect
  label="Países"
  value={[]}
  error
  errorMessage="Selecciona al menos uno"
  options={opts}
/>`}>
                            <MultiSelect label="Países" value={[]} onChange={() => {}} error errorMessage="Selecciona al menos uno" options={countryOptions} />
                        </Preview>

                        <Preview label="Disabled con selección previa"
                            code={`<MultiSelect
  label="Países (bloqueado)"
  value={[{ name: "México", value: "mx" }]}
  disabled
  options={opts}
/>`}>
                            <MultiSelect label="Países (bloqueado)" value={[{ name: "México", value: "mx" }]} onChange={() => {}} disabled options={countryOptions} />
                        </Preview>
                    </Grid>
                </Section>

                {/* ═══════════════════════════════ CALENDAR ═══════════════════════════════ */}
                <Section id="calendar" badge="atom" title="Calendar"
                    description='Selector de fecha y hora con calendario visual flotante. Acepta múltiples formatos de entrada y siempre emite en formato "yyyy-mm-dd hh:mm:ss".'>

                    <PropsTable rows={[
                        { name: "label",        type: "string",      desc: "Texto que aparece encima del campo para identificar qué fecha se está registrando." },
                        { name: "value",        type: "string|Date", desc: "Controla la fecha mostrada. Acepta tres formatos: string 'yyyy-mm-dd hh:mm:ss', ISO string o un objeto Date nativo de JavaScript." },
                        { name: "onChange",     type: "function",    desc: "Se ejecuta al confirmar una fecha. Siempre emite un string en formato 'yyyy-mm-dd hh:mm:ss', sin importar el formato con que se inicializó." },
                        { name: "withTime",     type: "bool",        default: "false", desc: "Muestra selectores de hora, minuto y segundo junto al calendario. Actívalo cuando necesitas registrar un momento exacto, no solo una fecha." },
                        { name: "onlyTime",     type: "bool",        default: "false", desc: "Reemplaza el calendario por un selector de hora puro (HH:MM:SS). El campo muestra solo hh:mm:ss, el ícono cambia a reloj y onChange emite un string en formato 'hh:mm:ss'. Úsalo cuando solo necesitas capturar una hora, sin fecha." },
                        { name: "disabled",     type: "bool",        default: "false", desc: "Bloquea el campo visualmente e impide que el usuario abra el calendario." },
                        { name: "error",        type: "bool",        default: "false", desc: "Cambia el borde a rojo y hace visible el mensaje de error." },
                        { name: "errorMessage", type: "string",      desc: "Mensaje que aparece debajo del campo cuando error=true." },
                    ]} />

                    <Grid>
                        <Preview label="Solo fecha"
                            code={`<Calendar
  label="Fecha de inicio"
  value={date}
  onChange={setDate}
/>`}>
                            <Calendar label="Fecha de inicio" value={calDate} onChange={setCalDate} />
                        </Preview>

                        <Preview label="Fecha y hora (withTime)"
                            code={`<Calendar
  label="Fecha y hora exacta"
  value={dt}
  onChange={setDt}
  withTime
/>`}>
                            <Calendar label="Fecha y hora exacta" value={calDateTime} onChange={setCalDateTime} withTime />
                        </Preview>

                        <Preview label="Estado de error"
                            code={`<Calendar
  label="Fecha de vencimiento"
  value=""
  error
  errorMessage="La fecha es obligatoria"
/>`}>
                            <Calendar label="Fecha de vencimiento" value="" onChange={() => {}} error errorMessage="La fecha es obligatoria" />
                        </Preview>

                        <Preview label="Disabled"
                            code={`<Calendar
  label="Fecha de registro"
  value="2025-01-15 00:00:00"
  disabled
/>`}>
                            <Calendar label="Fecha de registro" value="2025-01-15 00:00:00" onChange={() => {}} disabled />
                        </Preview>
                    </Grid>
                </Section>

                {/* ═══════════════════════════════ CHECKBOX ═══════════════════════════════ */}
                <Section id="checkbox" badge="atom" title="Checkbox"
                    description="Casilla de verificación personalizada con estado indeterminado nativo (inputRef.indeterminate) y soporte de label. Útil para controles de selección parcial.">

                    <PropsTable rows={[
                        { name: "checked",       type: "bool",     default: "false", desc: "Controla si la casilla está marcada (true) o desmarcada (false). Siémpre úsalo con onChange para mantener el estado sincronizado." },
                        { name: "onChange",      type: "function", desc: "Se ejecuta al hacer clic. Recibe el evento nativo; usa e.target.checked para obtener el nuevo valor booleano." },
                        { name: "indeterminate", type: "bool",     default: "false", desc: "Muestra el símbolo '—' en lugar de una marca. Úsaló en checkboxes de 'Seleccionar todo' cuando solo una parte de los ítems está seleccionada." },
                        { name: "disabled",      type: "bool",     default: "false", desc: "Bloquea la casilla visualmente e impide que el usuario cambie su estado." },
                        { name: "label",         type: "string",   desc: "Texto descriptivo que aparece a la derecha de la casilla. Al hacer clic sobre el texto también se activa el checkbox." },
                    ]} />

                    <Preview
                        label="Todos los estados"
                        code={`// Desmarcado (controlado)
<Checkbox checked={val} onChange={e => setVal(e.target.checked)} label="Sin marcar" />

// Marcado
<Checkbox checked={true} onChange={fn} label="Marcado" />

// Indeterminado — útil en "Seleccionar todo" con selección parcial
<Checkbox checked={false} indeterminate onChange={fn} label="Indeterminado" />

// Deshabilitados
<Checkbox checked disabled label="Deshabilitado marcado" />
<Checkbox checked={false} disabled label="Deshabilitado desmarcado" />`}>
                        <Checkbox checked={checkA} onChange={e => setCheckA(e.target.checked)} label="Sin marcar (click para cambiar)" />
                        <Checkbox checked={checkB} onChange={e => setCheckB(e.target.checked)} label="Marcado (click para desmarcar)" />
                        <Checkbox checked={false} indeterminate onChange={() => {}} label="Indeterminado" />
                        <Checkbox checked disabled label="Deshabilitado marcado" />
                        <Checkbox checked={false} disabled label="Deshabilitado desmarcado" />
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ AVATAR ═══════════════════════════════ */}
                <Section id="avatar" badge="atom" title="Avatar"
                    description="Muestra las iniciales de un nombre o una imagen de perfil dentro de un círculo. La lógica de iniciales extrae: 2 palabras → 2 iniciales; 3+ palabras → 1ª y 3ª inicial.">

                    <PropsTable rows={[
                        { name: "label",     type: "string",  desc: "Nombre del usuario a partir del cual se generan las iniciales automáticamente. Con 2 palabras toma la 1ª y 2ª inicial; con 3+ toma la 1ª y 3ª." },
                        { name: "src",       type: "string",  desc: "URL de una imagen de perfil. Cuando se pasa, reemplaza las iniciales mostrando la foto completa dentro del círculo." },
                        { name: "alt",       type: "string",  desc: "Describe la imagen para lectores de pantalla. Solo se usa cuando src está presente." },
                        { name: "size",      type: "string",  default: '"medium"', desc: "Controla el diámetro del círculo y el tamaño de las iniciales o imagen. Disponible: small · medium · large." },
                        { name: "className", type: "string",  desc: "Agrega clases CSS extra al contenedor del avatar." },
                    ]} />

                    <Preview label="Iniciales de nombres: 1 word · 2 words · 3+ words">
                        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                            <div style={{ textAlign: "center" }}>
                                <Avatar label="Carlos" />
                                <Text size="extra-small" style={{ marginTop: 4 }}>"Carlos"</Text>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <Avatar label="Juan Pérez" />
                                <Text size="extra-small" style={{ marginTop: 4 }}>"Juan Pérez"</Text>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <Avatar label="María López Ramírez" />
                                <Text size="extra-small" style={{ marginTop: 4 }}>"María López Ramírez"</Text>
                            </div>
                        </div>
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ STATUS ═══════════════════════════════ */}
                <Section id="status" badge="atom" title="Status"
                    description="Chip / badge para representar visualmente el estado de un elemento con colores semánticos. Texto en uppercase con borde y fondo tintado.">

                    <PropsTable rows={[
                        { name: "severity", type: "string",    default: '"primary"', desc: "Determina el esquema de colores del badge: primary = azul, success = verde, error = rojo, warning = naranja, default = gris neutro." },
                        { name: "children", type: "ReactNode", desc: "Texto o elemento que se muestra dentro del badge. Se renderiza en mayúsculas." },
                    ]} />

                    <Preview
                        label="Todas las severidades"
                        code={`<Status severity="primary">Activo</Status>
<Status severity="success">Completado</Status>
<Status severity="error">Error</Status>
<Status severity="warning">Pendiente</Status>
<Status severity="default">Inactivo</Status>`}>
                        <Status severity="primary">Activo</Status>
                        <Status severity="success">Completado</Status>
                        <Status severity="error">Error</Status>
                        <Status severity="warning">Pendiente</Status>
                        <Status severity="default">Inactivo</Status>
                    </Preview>

                    <Preview
                        label="Casos de uso típicos"
                        code={`<Status severity="success">Aprobado</Status>
<Status severity="warning">En revisión</Status>
<Status severity="error">Rechazado</Status>
<Status severity="default">Borrador</Status>
<Status severity="primary">En proceso</Status>`}>
                        <Status severity="success">Aprobado</Status>
                        <Status severity="warning">En revisión</Status>
                        <Status severity="error">Rechazado</Status>
                        <Status severity="default">Borrador</Status>
                        <Status severity="primary">En proceso</Status>
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ TOAST ═══════════════════════════════ */}
                <Section id="toast" badge="atom" title="Toast"
                    description="Sistema de notificaciones temporales no intrusivas. Se controla mediante el hook useToast() y el componente ToastContainer que debe montarse una vez en el árbol.">

                    <SubSection title="Hook: useToast()">
                        <PropsTable rows={[
                            { name: "toasts",      type: "array",    desc: "Array interno que mantiene los toasts activos. Pásaló a <ToastContainer toasts={toasts}> para que se rendericen en pantalla." },
                            { name: "addToast",    type: "function", desc: "Crea y muestra un toast. Llámalo con message y opcionalmente type, duration, mode y template para personalizar su aspecto." },
                            { name: "removeToast", type: "function", desc: "Cierra un toast específico antes de que expire su duración. Úsaló para descartarlo manualmente o como respuesta a una acción del usuario." },
                        ]} />
                    </SubSection>

                    <SubSection title="addToast(message, type, duration, mode, template)">
                        <PropsTable rows={[
                            { name: "message",  type: "string",    desc: "Texto principal que ve el usuario en el toast." },
                            { name: "type",     type: "string",    default: '"info"',  desc: "Define el ícono y los colores del toast. info = azul, success = verde, error = rojo, warning = naranja." },
                            { name: "duration", type: "number",    default: "4000",    desc: "Cuánto tiempo en ms permanece visible el toast antes de desaparecer. Aumenta el valor para mensajes que el usuario debe leer con más calma." },
                            { name: "mode",     type: "string",    default: '"dark"',  desc: "Cambia el fondo del toast. dark = oscuro (predeterminado), light = claro. Elige según el contraste con el fondo de tu interfaz." },
                            { name: "template", type: "ReactNode", default: "null",    desc: "Reemplaza el texto simple del toast con contenido JSX libre. Úsaló cuando necesitas un layout personalizado con botones, íconos o más información." },
                        ]} />
                    </SubSection>

                    <SubSection title="Componente: ToastContainer">
                        <PropsTable rows={[
                            { name: "toasts",      type: "array",    desc: "Recibe el array toasts que retorna useToast(). Sin él no se renderizan los toasts." },
                            { name: "removeToast", type: "function", desc: "Recibe la función removeToast del hook. La usa internamente para desmontar cada toast cuando termina su duración o el usuario lo cierra." },
                        ]} />
                    </SubSection>

                    <Preview
                        label="Click en los botones para disparar toasts"
                        code={`// 1. Hook al nivel del componente
const { toasts, addToast, removeToast } = useToast();

// 2. Disparar toasts
addToast("Operación exitosa", "success");
addToast("Revisa los datos", "warning", 6000);
addToast("Error del servidor", "error", 5000, "light");

// 3. Montar el contenedor (una vez, puede ser en el layout)
<ToastContainer toasts={toasts} removeToast={removeToast} />`}>
                        <Button icon="info"    onClick={() => addToast("Información de prueba.", "info")}>Info</Button>
                        <Button severity="success" onClick={() => addToast("Guardado correctamente.", "success")}>Success</Button>
                        <Button severity="error"   onClick={() => addToast("Ocurrió un error inesperado.", "error", 5000)}>Error</Button>
                        <Button severity="warning" onClick={() => addToast("Revisa los datos antes de continuar.", "warning")}>Warning</Button>
                        <Button outline            onClick={() => addToast("Notificación en modo claro.", "info", 4000, "light")}>Light mode</Button>
                    </Preview>
                    <ToastContainer toasts={toasts} removeToast={removeToast} />
                </Section>

                {/* ═══════════════════════════════ TOOLTIP ═══════════════════════════════ */}
                <Section id="tooltip" badge="atom" title="Tooltip"
                    description="Muestra contenido flotante al hacer hover sobre el elemento hijo. Usa createPortal para evitar problemas de z-index. Se posiciona automáticamente arriba o abajo según el espacio disponible. Incluye efecto visual Mercury Drop.">

                    <PropsTable rows={[
                        { name: "content",  type: "string|ReactNode", desc: "Define lo que aparece en la burbuja flotante. Puede ser un string simple o JSX con estilos, íconos y más." },
                        { name: "children", type: "ReactNode",        desc: "Elemento sobre el que el usuario hace hover para ver el tooltip. Puede ser cualquier componente o elemento HTML." },
                    ]} />

                    <Preview
                        label="Hover sobre los elementos"
                        code={`// Texto simple
<Tooltip content="Eliminar este registro permanentemente">
  <ActionButton icon="delete" />
</Tooltip>

// Contenido JSX
<Tooltip content={<><strong>Atención:</strong> acción irreversible</>}>
  <Button>Acción crítica</Button>
</Tooltip>

// Sobre cualquier elemento
<Tooltip content="Haz click para continuar">
  <Button>Hover aquí</Button>
</Tooltip>`}>
                        <Tooltip content="Tooltip básico de texto plano">
                            <Button>Texto simple</Button>
                        </Tooltip>
                        <Tooltip content={<><strong>Atención:</strong> esta acción es <em>irreversible</em></>}>
                            <Button severity="error">Con JSX</Button>
                        </Tooltip>
                        <Tooltip content="Editar este elemento">
                            <ActionButton icon="edit" />
                        </Tooltip>
                        <Tooltip content="Descargar reporte en PDF">
                            <ActionButton icon="download" />
                        </Tooltip>
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ ANIMATION ICONS ═══════════════════════════════ */}
                <Section id="animation-icons" badge="atom" title="AnimationIcons"
                    description="Ícono de Material Icons con animaciones CSS integradas. Diseñado para estados de carga, spinners de actividad o indicadores visuales en movimiento.">

                    <PropsTable rows={[
                        { name: "animation", type: "string",  default: '"rotate"',           desc: "Define qué tipo de animación se aplica al ícono. Actualmente solo disponible 'rotate'." },
                        { name: "direction", type: "string",  default: '"right"',            desc: "Controla el sentido de giro: right = horario (predeterminado), left = antihorario." },
                        { name: "velocity",  type: "number",  default: "1",                  desc: "Duración en segundos de cada vuelta completa. A menor valor el ícono gira más rápido. Ej: 0.4 = muy rápido, 3 = lento." },
                        { name: "icon",      type: "string",  default: '"progress_activity"', desc: "Nombre del ícono de Material Icons que se anima. Cámbialo para representar distintos tipos de operación (carga, sincronización, actualización)." },
                        { name: "size",      type: "string",  desc: "Tamaño visual del ícono. Acepta cualquier valor CSS válido: px, rem, em." },
                        { name: "color",     type: "string",  desc: "Aplica un color específico al ícono sin usar severity. Acepta hex, rgb o variables CSS." },
                        { name: "severity",  type: "string",  desc: "Aplica un color semántico predefinido al ícono sin especificar un valor hex. success = verde, error = rojo, warning = naranja." },
                        { name: "className", type: "string",  default: '""', desc: "Agrega clases CSS extra al contenedor del ícono." },
                    ]} />

                    <SubSection title="Dirección y velocidad">
                        <Preview
                            label="right · left · 0.4s (rápido) · 3s (lento)"
                            code={`<AnimationIcons direction="right" velocity={1} />
<AnimationIcons direction="left"  velocity={1} />
<AnimationIcons velocity={0.4} icon="autorenew" />
<AnimationIcons velocity={3}   icon="sync" />`}>
                            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                                {[
                                    { dir: "right", vel: 1,   icon: "progress_activity", label: "right / 1s" },
                                    { dir: "left",  vel: 1,   icon: "progress_activity", label: "left / 1s"  },
                                    { dir: "right", vel: 0.4, icon: "autorenew",          label: "0.4s"       },
                                    { dir: "right", vel: 3,   icon: "sync",               label: "3s"         },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                        <AnimationIcons direction={item.dir} velocity={item.vel} icon={item.icon} size="28px" />
                                        <Text size="extra-small" color="var(--gray-dark-500)">{item.label}</Text>
                                    </div>
                                ))}
                            </div>
                        </Preview>
                    </SubSection>

                    <SubSection title="Severidades y color directo">
                        <Preview
                            label="success · error · warning · color personalizado"
                            code={`<AnimationIcons severity="success" icon="refresh"        size="28px" />
<AnimationIcons severity="error"   icon="sync_problem"  size="28px" />
<AnimationIcons severity="warning" icon="autorenew"     size="28px" />
<AnimationIcons color="#9c27b0"    icon="progress_activity" size="28px" />`}>
                            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                                {[
                                    { severity: "success", icon: "refresh",           label: "success",   color: undefined     },
                                    { severity: "error",   icon: "sync_problem",      label: "error",     color: undefined     },
                                    { severity: "warning", icon: "autorenew",         label: "warning",   color: undefined     },
                                    { severity: undefined, icon: "progress_activity", label: "#9c27b0", color: "#9c27b0"    },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                        <AnimationIcons severity={item.severity} icon={item.icon} size="28px" color={item.color} />
                                        <Text size="extra-small" color="var(--gray-dark-500)">{item.label}</Text>
                                    </div>
                                ))}
                            </div>
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ MODAL ═══════════════════════════════ */}
                <Section id="modal" badge="atom" title="Modal"
                    description="Ventana modal con overlay oscuro, cabecera con título, botón de cierre y área de contenido libre. Se cierra haciendo click en el overlay o en el botón ✕.">

                    <PropsTable rows={[
                        { name: "title",    type: "string",    desc: "Texto que aparece en la barra superior del modal, identifica la acción o contexto de lo que se muestra." },
                        { name: "children", type: "ReactNode", desc: "Cualquier elemento o componente que se renderiza dentro del área principal del modal." },
                        { name: "onClose",  type: "function",  desc: "Se ejecuta cuando el usuario cierra el modal, ya sea haciendo clic en el fondo oscuro o en el botón ✕. Úsaló para desmontar el modal cambiando el estado a false." },
                    ]} />

                    <Preview
                        label="Click en el botón para abrir el modal"
                        code={`const [open, setOpen] = useState(false);

<Button icon="open_in_new" onClick={() => setOpen(true)}>
  Abrir Modal
</Button>

{open && (
  <Modal title="Confirmar acción" onClose={() => setOpen(false)}>
    <Text>¿Estás seguro de que deseas continuar?</Text>
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
      <Button outline onClick={() => setOpen(false)}>Cancelar</Button>
      <Button severity="success" onClick={() => setOpen(false)}>Confirmar</Button>
    </div>
  </Modal>
)}`}>
                        <Button icon="open_in_new" onClick={() => setModalOpen(true)}>Abrir Modal</Button>
                    </Preview>

                    {modalOpen && (
                        <Modal title="Ejemplo de Modal" onClose={() => setModalOpen(false)}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <Text>Este es el contenido del modal. Puedes incluir cualquier componente dentro.</Text>
                                <Input label="Campo de ejemplo" placeholder="Escribe algo..." value={inputVal} onChange={e => setInputVal(e.target.value)} />
                                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                    <Button outline onClick={() => setModalOpen(false)}>Cancelar</Button>
                                    <Button severity="success" onClick={() => setModalOpen(false)}>Confirmar</Button>
                                </div>
                            </div>
                        </Modal>
                    )}
                </Section>

                {/* ═══════════════════════════════ CONFIRM ════════════════════════════════ */}
                <Section id="confirm" badge="atom" title="Confirm"
                    description="Diálogo de confirmación estilo iOS con efecto liquid crystal glassmorphism. Ideal para acciones irreversibles que requieren confirmación explícita del usuario. Se renderiza en un portal sobre el body.">

                    <PropsTable rows={[
                        { name: "open",        type: "bool",     default: "false",        desc: "Controla si el diálogo está visible. Mantenlo en estado con useState." },
                        { name: "title",       type: "string",   desc: "Título principal del diálogo. Aparece en negrita centrado." },
                        { name: "message",     type: "string",   desc: "Mensaje descriptivo que explica la acción al usuario. Aparece debajo del título." },
                        { name: "icon",        type: "string",   desc: "Nombre de ícono Material Icons que aparece centrado encima del título. Omítelo para mostrar solo texto." },
                        { name: "onConfirm",   type: "function", desc: "Se ejecuta al presionar el botón de aceptar. Úsalo para cerrar el diálogo (setOpen(false)) y ejecutar la acción confirmada." },
                        { name: "onClose",     type: "function", desc: "Se ejecuta al presionar cancelar, hacer clic fuera del diálogo o pulsar Escape." },
                        { name: "confirmText", type: "string",   default: '"Aceptar"',    desc: "Texto del botón de confirmación (derecha, azul negrita)." },
                        { name: "cancelText",  type: "string",   default: '"Cancelar"',   desc: "Texto del botón de cancelación (izquierda, gris)." },
                        { name: "requireWord", type: "string",   desc: "Palabra exacta que el usuario debe escribir para habilitar el botón de confirmación. El botón permanece deshabilitado hasta que coincida. También acepta Enter." },
                    ]} />

                    <div className="docs-note docs-note--warning">
                        <span className="icon" data-icon="warning" />
                        <Text size="small">
                            <strong>Obligatorio:</strong> montar <code>{"{ConfirmDialog}"}</code> en el JSX del componente.
                            Sin este paso el portal nunca se renderiza y el diálogo no aparece,
                            aunque <code>confirm()</code> se llame correctamente y la promesa quede pendiente.
                        </Text>
                    </div>

                    <SubSection title="Uso imperativo con Promise (useConfirm)">
                        <Preview
                            label="confirm() devuelve Promise<boolean> — la ejecución espera la respuesta del usuario"
                            code={`import { useConfirm } from "@/Components/Atoms/Confirm/Confirm";

const { confirm, ConfirmDialog } = useConfirm();

const handleDelete = async () => {
  const ok = await confirm({
    icon: "delete",
    title: "¿Eliminar registro?",
    message: "Esta acción no se puede deshacer.",
    confirmText: "Eliminar",
  });
  if (!ok) return;   // usuario canceló → no se ejecuta nada
  // ... lógica de eliminación
};

return (
  <>
    <Button severity="error" icon="delete" onClick={handleDelete}>
      Eliminar registro
    </Button>
    {ConfirmDialog}
  </>
);`}>
                            <Button severity="error" icon="delete" onClick={async () => {
                                const ok = await confirm({
                                    icon: "delete",
                                    title: "¿Eliminar registro?",
                                    message: "Esta acción no se puede deshacer.",
                                    confirmText: "Eliminar",
                                });
                                if (!ok) return;
                                // acción confirmada
                            }}>
                                Eliminar registro
                            </Button>
                            {ConfirmDialog}
                        </Preview>
                    </SubSection>

                    <SubSection title="Confirmación con palabra clave (requireWord)">
                        <Preview
                            label='El botón se habilita solo cuando el usuario escribe exactamente la palabra indicada'
                            code={`const ok = await confirm({
  icon: "warning",
  title: "Cancelar servicio",
  message: 'Esta acción es irreversible. Escribe "CANCELAR" para confirmar.',
  requireWord: "CANCELAR",
  confirmText: "Cancelar servicio",
  cancelText: "Volver",
});
if (!ok) return;
// ... cancelar servicio`}>
                            <Button severity="warning" icon="warning" onClick={async () => {
                                await confirm({
                                    icon: "warning",
                                    title: "Cancelar servicio",
                                    message: 'Esta acción es irreversible. Escribe "CANCELAR" para confirmar.',
                                    requireWord: "CANCELAR",
                                    confirmText: "Cancelar servicio",
                                    cancelText: "Volver",
                                });
                            }}>
                                Acción con palabra clave
                            </Button>
                        </Preview>
                    </SubSection>

                    <SubSection title="Uso controlado (legacy)">
                        <Preview
                            label="Confirm con ícono y mensaje de advertencia"
                            code={`const [open, setOpen] = useState(false);

<Button severity="error" icon="delete" onClick={() => setOpen(true)}>
  Abrir Confirm
</Button>

<Confirm
  open={open}
  icon="warning"
  title="¿Eliminar registro?"
  message="Esta acción no se puede deshacer. ¿Deseas continuar?"
  confirmText="Eliminar"
  cancelText="Cancelar"
  onConfirm={() => setOpen(false)}
  onClose={() => setOpen(false)}
/>`}>
                            <Button severity="error" icon="delete" onClick={() => setConfirmOpen(true)}>
                                Abrir Confirm
                            </Button>
                            <Confirm
                                open={confirmOpen}
                                icon="warning"
                                title="¿Eliminar registro?"
                                message="Esta acción no se puede deshacer. ¿Deseas continuar?"
                                confirmText="Eliminar"
                                cancelText="Cancelar"
                                onConfirm={() => setConfirmOpen(false)}
                                onClose={() => setConfirmOpen(false)}
                            />
                        </Preview>
                    </SubSection>
                </Section>

                {/* ════════════════════════════ EMPTY STATE ════════════════════════════ */}
                <Section id="empty-state" badge="atom" title="EmptyState"
                    description="Indicador de estado vacío con guía de pasos. Se usa para rellenar el espacio de una vista cuando aún no hay datos, orientando al usuario sobre qué debe hacer a continuación.">

                    <PropsTable rows={[
                        { name: "icon",      type: "string",   default: '"inbox"', desc: "Nombre del ícono Material Symbols que aparece centrado arriba del título." },
                        { name: "title",     type: "string",   desc: "Mensaje principal que orienta al usuario. Se muestra debajo del ícono." },
                        { name: "steps",     type: "array",    default: "[]",      desc: "Lista de pasos: [{ icon: string, label: ReactNode }]. Cada elemento se renderiza como un chip numerado con ícono." },
                        { name: "className", type: "string",   default: '""',      desc: "Clases CSS adicionales para el contenedor." },
                    ]} />

                    <SubSection title="Con pasos">
                        <Preview
                            label="Empty state con guía de pasos"
                            code={`<EmptyState
    icon="search"
    title="Aplica filtros para ver resultados"
    steps={[
        { icon: "person",    label: <>Elige el <strong>cliente</strong></> },
        { icon: "alt_route", label: <>Selecciona la <strong>ruta</strong></> },
        { icon: "done_all",  label: <>Clic en <strong>Buscar</strong></> },
    ]}
/>`}>
                            <EmptyState
                                icon="search"
                                title="Aplica filtros para ver resultados"
                                steps={[
                                    { icon: "person",    label: <><strong>cliente</strong></> },
                                    { icon: "alt_route", label: <>Selecciona la <strong>ruta</strong></> },
                                    { icon: "done_all",  label: <>Clic en <strong>Buscar</strong></> },
                                ]}
                            />
                        </Preview>
                    </SubSection>

                    <SubSection title="Solo ícono y título">
                        <Preview
                            label="Sin pasos"
                            code={`<EmptyState icon="inbox" title="No hay registros aún" />`}>
                            <EmptyState icon="inbox" title="No hay registros aún" />
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ COLLAPSE ═══════════════════════════════ */}
                <Section id="collapse" badge="molecule" title="Collapse"
                    description="Panel plegable con animación de apertura/cierre. Muestra ícono, título y chevron de estado. El cuerpo se expande y contrae suavemente.">

                    <PropsTable rows={[
                        { name: "icon",        type: "string",    desc: "Ícono de Material Icons que aparece a la izquierda del título en la cabecera. Ayuda a identificar visualmente el tipo de contenido del panel." },
                        { name: "title",       type: "string",    desc: "Texto que aparece en la cabecera y sirve de botón para expandir o colapsar el panel." },
                        { name: "children",    type: "ReactNode", desc: "Contenido que se muestra u oculta al hacer clic en la cabecera. Puede ser cualquier componente o estructura JSX." },
                        { name: "defaultOpen", type: "bool",      default: "true", desc: "Controla si el panel empieza expandido (true) o contraído (false). Solo se aplica al montarse; después el usuario controla el estado." },
                        { name: "className",   type: "string",    default: '""', desc: "Agrega clases CSS extra al contenedor del Collapse." },
                    ]} />

                    <Preview
                        label="defaultOpen=true · defaultOpen=false"
                        code={`// Abierto por defecto
<Collapse icon="info" title="Panel abierto por defecto">
  <Text>Contenido visible al cargar la página.</Text>
</Collapse>

// Cerrado por defecto
<Collapse icon="settings" title="Configuración avanzada" defaultOpen={false}>
  <Text>Contenido oculto hasta que el usuario haga click.</Text>
</Collapse>`}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
                            <Collapse icon="info" title="Panel abierto por defecto (defaultOpen=true)">
                                <Text color="var(--gray-dark-600)">
                                    Este contenido es visible desde el inicio porque defaultOpen es true.
                                    Puedes colocar cualquier componente dentro del cuerpo del Collapse.
                                </Text>
                            </Collapse>
                            <Collapse icon="settings" title="Panel cerrado por defecto (defaultOpen=false)" defaultOpen={false}>
                                <Text color="var(--gray-dark-600)">
                                    Este contenido estaba oculto. Se reveló al hacer click en la cabecera.
                                </Text>
                            </Collapse>
                            <Collapse icon="layers" title="Collapse con componentes anidados" defaultOpen={false}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <Status severity="success">Completado</Status>
                                    <Status severity="warning">Pendiente</Status>
                                    <Status severity="error">Error</Status>
                                    <Button icon="refresh" outline>Actualizar</Button>
                                </div>
                            </Collapse>
                        </div>
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ STEPS ═══════════════════════════════ */}
                <Section id="steps" badge="molecule" title="Steps"
                    description="Indicador de progreso por pasos numerados. Soporta orientación vertical y horizontal, íconos por paso, descripciones y control dinámico del estado activo.">

                    <PropsTable rows={[
                        { name: "steps",      type: "array",  desc: "Define los pasos a mostrar. Cada objeto requiere label (texto) y opcionalmente description (subtexto bajo el label) e icon (nombre de Material Icons)." },
                        { name: "direction",  type: "string", default: '"vertical"', desc: "Controla el eje sobre el que se despliegan los pasos. vertical = apilados en columna, horizontal = en fila." },
                        { name: "activeStep", type: "number", desc: "Indica cuántos pasos están marcados como completados (base 1). Pasa -1 para ninguno, un número para marcar hasta ese paso, o undefined para marcarlos todos." },
                    ]} />

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small" color="var(--blue-700)">
                            <strong>activeStep es base 1:</strong> activeStep=1 marca solo el primer paso. activeStep=3 marca los primeros tres. activeStep=0 activa el primero sin completar ninguno.
                        </Text>
                    </div>

                    <SubSection title="Vertical con control dinámico">
                        <Preview
                            label={`activeStep={${stepsActive === undefined ? "undefined (todos ✓)" : stepsActive}} — usa los botones para cambiar`}
                            code={`const steps = [
  { label: "Configurar cuenta",   description: "Ingresa tus datos.",        icon: "manage_accounts" },
  { label: "Verificar identidad", description: "Confirma tu correo.",        icon: "verified_user"   },
  { label: "Personalizar perfil", description: "Agrega foto.",               icon: "person"          },
  { label: "Completar",           description: "Tu cuenta está lista.",      icon: "check_circle"    },
];

<Steps steps={steps} activeStep={activeStep} />`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <Button outline={stepsActive !== -1}  onClick={() => setStepsActive(-1)}>Ninguno (-1)</Button>
                                    <Button outline={stepsActive !== 0}   onClick={() => setStepsActive(0)}>Paso 0 (activa 1ro)</Button>
                                    {stepsData.map((_, i) => (
                                        <Button key={i} outline={stepsActive !== i + 1} onClick={() => setStepsActive(i + 1)}>
                                            Paso {i + 1}
                                        </Button>
                                    ))}
                                    <Button outline={stepsActive !== undefined} onClick={() => setStepsActive(undefined)}>Todos ✓</Button>
                                </div>
                                <Steps steps={stepsData} activeStep={stepsActive} />
                            </div>
                        </Preview>
                    </SubSection>

                    <SubSection title="Horizontal">
                        <Preview
                            label='direction="horizontal" — activeStep={2}'
                            code={`<Steps steps={steps} activeStep={2} direction="horizontal" />`}>
                            <Steps steps={stepsData} activeStep={2} direction="horizontal" />
                        </Preview>
                    </SubSection>

                    <SubSection title="Steps simples (sin description ni icon)">
                        <Preview
                            label="Solo label — todos completados"
                            code={`<Steps
  steps={[
    { label: "Solicitud" },
    { label: "Revisión" },
    { label: "Aprobación" },
    { label: "Publicado" },
  ]}
/>`}>
                            <Steps steps={[
                                { label: "Solicitud" },
                                { label: "Revisión" },
                                { label: "Aprobación" },
                                { label: "Publicado" },
                            ]} />
                        </Preview>
                    </SubSection>
                </Section>

                {/* ═══════════════════════════════ OPTIONS GROUP ═══════════════════════════════ */}
                <Section id="options-group" badge="molecule" title="OptionsGroup"
                    description="Grupo de botones de selección única (radio visual). Resalta la opción activa y llama a onSelect con el value correspondiente al hacer click.">

                    <PropsTable rows={[
                        { name: "label",       type: "string",   desc: "Título que aparece encima del grupo de botones para indicar qué está seleccionando el usuario. Si no se pasa, el grupo se renderiza sin encabezado." },
                        { name: "options",     type: "array",    desc: "Array de opciones que se renderizan como botones. Cada objeto necesita label (texto visible) y value (identificador que se emite al seleccionar)." },
                        { name: "onSelect",    type: "function", desc: "Se ejecuta al hacer clic en cualquier opción. Recibe el value del objeto seleccionado para que puedas actualizar el estado." },
                        { name: "activeValue", type: "any",      desc: "Value de la opción que debe mostrarse resaltada. Se compara con option.value para determinar cuál botón está activo." },
                    ]} />

                    <Preview
                        label="Selector de período — click para cambiar"
                        code={`const [active, setActive] = useState("week");

<OptionsGroup
  options={[
    { label: "Día",    value: "day"   },
    { label: "Semana", value: "week"  },
    { label: "Mes",    value: "month" },
    { label: "Año",    value: "year"  },
  ]}
  activeValue={active}
  onSelect={setActive}
/>

// active recibe "day" | "week" | "month" | "year"`}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <OptionsGroup
                                options={[
                                    { label: "Día",    value: "day"   },
                                    { label: "Semana", value: "week"  },
                                    { label: "Mes",    value: "month" },
                                    { label: "Año",    value: "year"  },
                                ]}
                                activeValue={optionsVal}
                                onSelect={setOptionsVal}
                            />
                            <Text size="small" color="var(--gray-dark-500)">
                                Valor activo: <strong>{optionsVal}</strong>
                            </Text>
                        </div>
                    </Preview>
                </Section>

                <Section id="sidebar" badge="molecule" title="Sidebar"
                    description="Menú lateral genérico reutilizable. Soporta grupos de navegación o lista plana, ítem activo resaltado, encabezado personalizable (brand), ancho configurable e ícono por ítem.">

                    <PropsTable rows={[
                        { name: "nav",       type: "array",     default: "[]",   desc: "Define la estructura del menú. Pasa grupos con items para menús con categorías [{ group, items }], o un array plano [{ id, label, icon? }] para navegación simple." },
                        { name: "activeId",  type: "string",    desc: "El id del ítem que se muestra resaltado como activo. Actúalizalo en onSelect para mantener la navegación sincronizada con la vista actual." },
                        { name: "onSelect",  type: "function",  desc: "Se ejecuta al hacer clic en un ítem. Recibe el id del ítem para que puedas actualizar activeId y navegar a la sección correspondiente." },
                        { name: "brand",     type: "ReactNode", desc: "Elemento que se muestra en la parte superior del sidebar, antes de los ítems. Úsaló para el logo o nombre de la aplicación." },
                        { name: "width",     type: "number",    default: "220",  desc: "Controla el ancho del sidebar en píxeles. Ajústalo según el espacio disponible en tu layout." },
                        { name: "className", type: "string",    default: '""',   desc: "Agrega clases CSS extra al elemento raíz del sidebar." },
                    ]} />

                    <SubSection title="Estructura de nav (agrupado)">
                        <pre className="docs-code"><code>{`[
  {
    group: "Principal",
    items: [
      { id: "dashboard", label: "Dashboard", icon: "dashboard" },
      { id: "reports",   label: "Reportes",  icon: "bar_chart" },
    ],
  },
  {
    group: "Configuración",
    items: [
      { id: "settings", label: "Ajustes", icon: "settings" },
    ],
  },
]`}</code></pre>
                    </SubSection>

                    <SubSection title="Estructura de nav (lista plana — sin grupos)">
                        <pre className="docs-code"><code>{`[
  { id: "home",     label: "Inicio",   icon: "home"     },
  { id: "profile", label: "Perfil",   icon: "person"   },
  { id: "logout",  label: "Salir",    icon: "logout"   },
]`}</code></pre>
                    </SubSection>

                    <SubSection title="Demo interactivo">
                        <SidebarDemo />
                    </SubSection>

                    <Preview
                        label="Uso básico"
                        code={`import Sidebar from "../Components/Molecules/Sidebar/Sidebar";

const [active, setActive] = useState("dashboard");

<Sidebar
  nav={[
    {
      group: "Principal",
      items: [
        { id: "dashboard", label: "Dashboard", icon: "dashboard" },
        { id: "reports",   label: "Reportes",  icon: "bar_chart" },
      ],
    },
  ]}
  activeId={active}
  onSelect={setActive}
  brand={<strong>Mi App</strong>}
/>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>
                </Section>

                {/* ════════════════════════════════════════════ HEADER ════ */}
                <Section id="header" badge="molecule" title="Header"
                    description="Barra superior del layout autenticado. Muestra el nombre de la sección actual (derivado de la URL), acciones rápidas (descargar manual, ayuda, cerrar sesión) y el avatar del usuario. Se integra con SidePanel para mostrar ayuda contextual por ruta.">

                    <PropsTable rows={[
                        { name: "setMobileMenuOpen", type: "function", desc: "Se llama cuando el usuario toca el ícono de hamburguesa en pantallas pequeñas. Úsaló para cambiar el estado que controla la visibilidad del SideMenu móvil." },
                    ]} />

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small">
                            <strong>Header</strong> es usado internamente por <code>AuthGrid</code>. No se instancia directamente en las vistas.
                            Obtiene el nombre del usuario mediante el custom hook <code>useUserData</code> y la ruta activa con <code>useLocation</code>.
                        </Text>
                    </div>

                    <SubSection title="Comportamiento interno">
                        <Preview label="Acciones disponibles" code={`// Header se conecta automáticamente al contexto de React Router
// Nombre de sección: derivado de pathname  (/planeacion/mi-vista → "Mi Vista")
// Ayuda contextual:  helpContent[pathname]  →  abre SidePanel con guía
// Descargar manual:  descarga PDF desde URL remota
// Cerrar sesión:     limpia sessionStorage y redirige a "/"`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {[
                                    { icon: "download",     label: "Descargar manual" },
                                    { icon: "help",         label: "Ayuda contextual (si existe contenido para la ruta)" },
                                    { icon: "logout",       label: "Cerrar sesión" },
                                    { icon: "menu",         label: "Abrir SideMenu (solo móvil)" },
                                ].map(a => (
                                    <div key={a.icon} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="icon" data-icon={a.icon} style={{ color: "var(--blue-500)" }} />
                                        <Text size="small">{a.label}</Text>
                                    </div>
                                ))}
                            </div>
                        </Preview>
                    </SubSection>
                </Section>

                {/* ════════════════════════════════════════════ TABLE ═════ */}
                <Section id="table" badge="molecule" title="Table"
                    description="Tabla de datos avanzada con ordenamiento, filtro global, paginación, selección múltiple, virtual scroll, ocultamiento responsivo de columnas y descarga a Excel. Las columnas se definen con el componente Column como children.">

                    <PropsTable rows={[
                        { name: "data",                type: "array",           default: "[]",      desc: "Fuente de datos de la tabla. Cada objeto del array se convierte en una fila; sus claves se acceden con el prop value de Column." },
                        { name: "children",            type: "Column[]",        desc: "Las columnas de la tabla. Cada <Column /> define cómo se muestra y comporta una propiedad de data." },
                        { name: "header",              type: "ReactNode",       desc: "Zona libre encima de la tabla. Úsalá para el título de la sección, botones de acción global o filtros adicionales." },
                        { name: "footer",              type: "ReactNode",       desc: "Zona libre debajo de la tabla. Útil para totales, resúmenes o acciones secundarias." },
                        { name: "stripedRows",         type: "boolean",         desc: "Añade un fondo alternado entre filas pares e impares para facilitar la lectura en tablas con muchas columnas." },
                        { name: "globalFilter",        type: "boolean",         desc: "Agrega un campo de búsqueda que filtra simultáneamente todas las columnas que tienen el prop value definido." },
                        { name: "paginator",           type: "boolean",         desc: "Divide los datos en páginas y muestra controles de navegación. Configura el tamaño de página con rows." },
                        { name: "rows",                type: "number",          default: "10",      desc: "Cuántas filas se muestran por página cuando paginator está activo." },
                        { name: "height",              type: "string|number",   default: "\"100vh\"", desc: "Altura del contenedor de la tabla. Con datos masivos activa el virtual scroll, que solo renderiza las filas visibles para mejorar el rendimiento." },
                        { name: "rowHeight",           type: "number",          default: "44",      desc: "Alto estimado en px de cada fila. El virtual scroll lo usa para calcular qué filas son visibles según el scroll actual." },
                        { name: "selectable",          type: "boolean",         desc: "Añade una columna de checkboxes en el extremo izquierdo. Al seleccionar filas, onSelectionChange recibe el array actualizado." },
                        { name: "onSelectionChange",   type: "function",        desc: "Se ejecuta cada vez que cambia la selección de filas. Recibe el array completo con los objetos de las filas seleccionadas actualmente." },
                        { name: "responsiveHideColumn",type: "boolean",         desc: "Activa el modo responsivo: las columnas marcadas con responsive={true} se ocultan automáticamente cuando la pantalla no tiene suficiente ancho." },
                        { name: "download",            type: "boolean|object",  desc: "Muestra un botón que descarga los datos a Excel. Pasa true para descarga básica, u objeto con columns (columnas a incluir), onlyFilter (solo filas visibles) y fileName (nombre del archivo)." },
                        { name: "onRowEdit",       type: "function", desc: "Se ejecuta cuando el usuario guarda los cambios de una fila en modo edición inline. Recibe el objeto completo de la fila con los nuevos valores y su índice. Actualiza data desde aquí." },
                        { name: "onDraftChange",   type: "function", desc: "Se llama en cada cambio de campo mientras la fila está en modo edición, antes de que se guarde. Recibe (field, value, draftActual) y puede devolver un objeto con overrides que se fusionan al draft. Útil para sincronizar campos relacionados (ej: al cambiar RESPONSABLE desde un select, actualizar RESPONSABLE_ID al mismo tiempo)." },
                        { name: "skeleton",            type: "boolean",         default: "false",   desc: "Muestra filas de carga animadas (shimmer) en lugar de datos reales. El número de filas se calcula automáticamente con base en height y rowHeight para simular la altura exacta de la tabla." },
                        { name: "columnSelect",        type: "string[]",        default: "null",    desc: "Array de values de columnas visibles por defecto. Activa un botón en la barra superior con menú de checkboxes para mostrar u ocultar cada columna individualmente. Las columnas cuyos values NO están en el array comienzan ocultas." },
                        { name: "showCounter",          type: "boolean",         default: "false",   desc: "Muestra un contador de registros en la barra superior. Cuando globalFilter está activo y hay texto de búsqueda, indica los registros filtrados sobre el total (ej. '12 de 50 registros'); sin búsqueda activa muestra solo el total." },
                    ]} />

                    <SubSection title="Props de Column">
                        <PropsTable rows={[
                            { name: "header",        type: "string",   desc: "Texto que aparece en la cabecera de la columna y sirve de botón de ordenamiento si sortable está activo." },
                            { name: "value",         type: "string",   desc: "Nombre de la propiedad del objeto de data que esta columna muestra. También habilita el ordenamiento y la búsqueda con globalFilter." },
                            { name: "body",          type: "function", desc: "Función de render personalizado: recibe el objeto de la fila y devuelve el JSX de la celda. Úsaló para añadir íconos, badges o botones de acción." },
                            { name: "sortable",      type: "boolean",  desc: "Hace clic en el encabezado para ordenar las filas ascendente o descendente por el value de esta columna." },
                            { name: "width",         type: "string",   desc: "Fija el ancho de la columna. Sin este prop la columna se distribuye proporcionalmente con las demás." },
                            { name: "align",         type: "string",   desc: "Controla la alineación horizontal del contenido de cada celda de esta columna: left, center o right." },
                            { name: "responsive",    type: "boolean",  desc: "Cuando responsiveHideColumn está activo en Table, esta columna se oculta automáticamente al reducirse la pantalla para preservar las columnas esenciales." },
                            { name: "edit",          type: "string",   desc: "Define qué tipo de campo se muestra al entrar en modo edición inline: text = campo de texto, checkbox = casilla, select = dropdown con editOptions, currency = input monetario con formato automático." },
                            { name: "editOptions",   type: "array",    desc: "Array de opciones del dropdown cuando edit=\"select\". Cada objeto necesita label (texto visible) y value (valor que se guarda)." },
                            { name: "editCondition", type: "function", desc: "Función que evalua la fila y determina si esa celda específica puede editarse. Devuelve true para permitir edición, false para bloquearla aunque edit esté definido." },
                            { name: "onClick", type: "callBack", desc: "Se ejecuta al hacer click en la celda. Recibe el objeto de la fila y su índice. Úsaló para abrir un modal, navegar a otra vista o mostrar detalles." },
                            { name: "tooltipHeader", type: "string",   desc: "Texto informativo que aparece como tooltip al hacer hover sobre el encabezado de la columna. Útil para explicar abreviaturas o aclarar el contenido de la columna." },
                        ]} />
                    </SubSection>

                    <SubSection title="Demo interactivo — selector de columnas">
                        <TableColumnSelectDemo />
                        <pre className="docs-code"><code>{`// Solo id, nombre y estado visibles por defecto
// El botón ⋯ en la barra permite activar/desactivar el resto

<Table data={data} columnSelect={["id", "nombre", "estado"]}>
  <Column header="ID"     value="id"     sortable />
  <Column header="Nombre" value="nombre" sortable />  {/* visible */}
  <Column header="Puesto" value="puesto" />           {/* oculta por defecto */}
  <Column header="Estado" value="estado" />           {/* visible */}
  <Column header="Activo" value="activo" />           {/* oculta por defecto */}
</Table>`.trim()}</code></pre>
                    </SubSection>

                    <SubSection title="Demo interactivo — skeleton">
                        <TableSkeletonDemo />
                    </SubSection>

                    <SubSection title="Demo interactivo — edición inline">
                        <TableEditDemo />
                    </SubSection>

                    <SubSection title="Demo interactivo — tabla básica">
                        <TableDemo />
                    </SubSection>

                    <Preview label="Edición inline — uso básico" code={`const [tableData, setTableData] = useState(data);

const handleRowEdit = (newRow, rowIndex) => {
  setTableData(prev => prev.map((r, i) => i === rowIndex ? newRow : r));
};

<Table data={tableData} onRowEdit={handleRowEdit}>
  {/* columna siempre editable */}
  <Column header="Nombre" value="nombre" edit="text" />

  {/* editable solo si activo === true */}
  <Column
    header="Puesto"
    value="puesto"
    edit="select"
    editOptions={[
      { label: "Analista",    value: "Analista"    },
      { label: "Supervisor",  value: "Supervisor"  },
    ]}
    editCondition={(row) => row.activo}
  />

    {/* input monetario con formato automático */}
    <Column
        header="Tarifa"
        value="tarifa"
        edit="currency"
    />

  {/* checkbox editable solo si no es admin */}
  <Column
    header="Activo"
    value="activo"
    edit="checkbox"
    editCondition={(row) => row.puesto !== "Admin"}
  />
</Table>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>

                    <Preview label="select con clave foránea (FK) — editLabelKey" code={`// Patrón recomendado: editLabelKey sincroniza el ID y la etiqueta automáticamente.
// No se necesita onDraftChange ni resolver etiquetas en handleRowEdit.

const opciones = data.usuarios.map(u => ({ value: u.ID, label: u.NOMBRE }));

const handleRowEdit = (newRow, rowIndex) => {
  setData(prev => prev.map((item, i) => i === rowIndex ? newRow : item));
  // newRow.ID_RESPONSABLE  → ID actualizado
  // newRow.RESPONSABLE     → etiqueta actualizada automáticamente
  api.update({ id: newRow.ID, responsable_id: newRow.ID_RESPONSABLE });
};

<Table data={data} onRowEdit={handleRowEdit}>
  <Column
    header="RESPONSABLE"
    value="ID_RESPONSABLE"       // campo ID que se guarda
    editLabelKey="RESPONSABLE"   // campo etiqueta que se sincroniza solo
    edit="select"
    body={(row) => row.RESPONSABLE}
    editOptions={opciones}
  />
</Table>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>
                </Section>

                {/* ════════════════════════════════════════════ SIDEDRAWER ═ */}
                <Section id="side-drawer" badge="molecule" title="SideDrawer"
                    description="Panel lateral colapsable horizontalmente. Envuelve cualquier contenido en una tarjeta con una tira de toggle (flecha) que comprime y expande el contenido con animación suave. Admite modo controlado y no controlado. La flecha permanece siempre visible gracias a position sticky, sin importar la altura del contenido.">

                    <PropsTable rows={[
                        { name: "collapsed",        type: "boolean",  default: "undefined", desc: "Controla desde el padre si el panel está colapsado (true) o expandido (false). Si no se pasa, el componente gestiona su propio estado internamente." },
                        { name: "onToggle",          type: "function", desc: "Se ejecuta al hacer clic en la tira de toggle. Recibe el nuevo valor (true = colapsar, false = expandir). En modo controlado actualiza tu estado aquí." },
                        { name: "defaultCollapsed",  type: "boolean",  default: "false",     desc: "Define si el panel empieza colapsado (true) o expandido (false) cuando se usa sin estado externo. Solo se aplica al montarse." },
                        { name: "width",             type: "number",   default: "320",        desc: "Ancho máximo del panel en píxeles. En modo inline controla cuánto ocupa en el layout; en modo modal define el ancho del panel deslizante." },
                        { name: "modalBreakpoint",   type: "number",   default: "undefined", desc: "Define el punto de quiebre en px. Cuando la pantalla es más estrecha que este valor, el panel deja de ocupar espacio en el layout y se abre como un overlay deslizante desde la derecha." },
                        { name: "contextClass",       type: "string",   default: '""',         desc: "Clase CSS del componente padre que se aplica como wrapper del contenido en modo modal. Necesario porque el portal rompe la cadena de selectores SCSS anidados. Pasa el nombre de la clase raíz de tu vista (ej: \"ProgramacionDeServicios\")." },
                        { name: "className",         type: "string",   default: '""',         desc: "Agrega clases CSS extra al contenedor raíz del SideDrawer." },
                        { name: "children",          type: "ReactNode", desc: "Contenido que vive dentro del panel. Se oculta y revela con animación al colapsar o expandir." },
                    ]} />

                    <SubSection title="Modo no controlado (defaultCollapsed)">
                        <Preview
                            label="El componente gestiona su propio estado"
                            code={`// Sin estado externo
<SideDrawer defaultCollapsed={false}>
  <div style={{ padding: 16 }}>
    <Text weight={700}>Panel de contenido</Text>
    <Text size="small" color="var(--gray-dark-500)">Cualquier contenido React.</Text>
  </div>
</SideDrawer>`}>
                            <SideDrawer defaultCollapsed={false} width={260}>
                                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                                    <Text weight={700}>Panel de contenido</Text>
                                    <Text size="small" color="var(--gray-dark-500)">Haz clic en la tira de la derecha para colapsar.</Text>
                                    <Status severity="success">Disponible</Status>
                                    <Status severity="error">No disponible</Status>
                                    <Status severity="warning">En proceso</Status>
                                </div>
                            </SideDrawer>
                        </Preview>
                    </SubSection>

                    <SubSection title="Modo controlado (collapsed + onToggle)">
                        {(() => {
                            const ControlledDemo = () => {
                                const [col, setCol] = useState(false);
                                return (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <Button onClick={() => setCol(false)} severity={!col ? undefined : "success"} outline={col}>Expandir</Button>
                                            <Button onClick={() => setCol(true)}  severity={col  ? "error" : undefined}   outline={!col}>Colapsar</Button>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                                            <SideDrawer collapsed={col} onToggle={setCol} width={260}>
                                                <div style={{ padding: 16 }}>
                                                    <Text weight={700}>Panel controlado</Text>
                                                    <Text size="small" color="var(--gray-dark-500)">Estado: <strong>{col ? "Colapsado" : "Expandido"}</strong></Text>
                                                </div>
                                            </SideDrawer>
                                        </div>
                                    </div>
                                );
                            };
                            return <ControlledDemo />;
                        })()}
                        <pre className="docs-code"><code>{`const [collapsed, setCollapsed] = useState(false);

<SideDrawer
  collapsed={collapsed}
  onToggle={setCollapsed}
  width={320}
>
  <aside>...</aside>
</SideDrawer>`.trim()}</code></pre>
                    </SubSection>

                    <SubSection title="Colapso automático por breakpoint">
                        <div className="docs-note">
                            <span className="icon" data-icon="info" />
                            <Text size="small">
                                Para que el panel se colapse automáticamente al reducir la pantalla,
                                usa modo controlado con un listener de <code>resize</code>:
                            </Text>
                        </div>
                        <pre className="docs-code"><code>{`const BREAKPOINT = 1580;
const [collapsed, setCollapsed] = useState(
  () => window.innerWidth <= BREAKPOINT
);

useEffect(() => {
  const fn = () => setCollapsed(window.innerWidth <= BREAKPOINT);
  window.addEventListener('resize', fn);
  return () => window.removeEventListener('resize', fn);
}, []);

<SideDrawer collapsed={collapsed} onToggle={() => setCollapsed(p => !p)}>
  <aside>...</aside>
</SideDrawer>`.trim()}</code></pre>
                    </SubSection>

                    <SubSection title="Modo modal por breakpoint (modalBreakpoint)">
                        <div className="docs-note">
                            <span className="icon" data-icon="info" />
                            <Text size="small">
                                Cuando <code>window.innerWidth &lt; modalBreakpoint</code>, el panel deja de ocupar
                                espacio en el layout y se muestra como un overlay deslizante desde la derecha
                                (igual que SidePanel). Un pequeño botón queda inline como trigger.
                                Al volver a un ancho mayor, regresa al modo inline automáticamente.
                            </Text>
                        </div>
                        {(() => {
                            const ModalBreakpointDemo = () => {
                                const [col, setCol] = useState(true);
                                return (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                        <div className="docs-note" style={{ margin: 0 }}>
                                            <span className="icon" data-icon="devices" />
                                            <Text size="small">
                                                Este demo tiene <code>modalBreakpoint=99999</code> para
                                                forzar siempre el modo modal y poder verlo en cualquier resolución.
                                                En producción usarías un valor real como <code>900</code>.
                                            </Text>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                                            <Text size="small" color="var(--gray-dark-500)" style={{ paddingTop: 8 }}>
                                                ← El botón trigger abre el modal
                                            </Text>
                                            <SideDrawer
                                                collapsed={col}
                                                onToggle={setCol}
                                                width={300}
                                                modalBreakpoint={99999}
                                            >
                                                <div style={{ padding: 16, paddingTop: 48, display: "flex", flexDirection: "column", gap: 8 }}>
                                                    <Text weight={700}>Panel como modal</Text>
                                                    <Text size="small" color="var(--gray-dark-500)">Overlay con backdrop y animación slide-in.</Text>
                                                    <Text size="small" color="var(--gray-dark-500)">Cierra con la X, Escape o clic fuera.</Text>
                                                    <Status severity="success">Disponible</Status>
                                                    <Status severity="warning">En proceso</Status>
                                                    <Status severity="error">No disponible</Status>
                                                </div>
                                            </SideDrawer>
                                        </div>
                                    </div>
                                );
                            };
                            return <ModalBreakpointDemo />;
                        })()}
                        <pre className="docs-code"><code>{`// El panel es inline en pantallas >= 900px
// y pasa a modal cuando la pantalla < 900px
<SideDrawer
  collapsed={collapsed}
  onToggle={setCollapsed}
  width={320}
  modalBreakpoint={900}
>
  <aside>...</aside>
</SideDrawer>

// Combinado con breakpoint de colapso automático:
const MODAL_BP   = 900;   // < 900px → modal
const INLINE_BP  = 1580;  // < 1580px → colapsar inline

const [collapsed, setCollapsed] = useState(
  () => window.innerWidth <= INLINE_BP
);
useEffect(() => {
  const fn = () => setCollapsed(window.innerWidth <= INLINE_BP);
  window.addEventListener('resize', fn);
  return () => window.removeEventListener('resize', fn);
}, []);

<SideDrawer
  collapsed={collapsed}
  onToggle={() => setCollapsed(p => !p)}
  modalBreakpoint={MODAL_BP}
>
  <aside>...</aside>
</SideDrawer>`.trim()}</code></pre>
                    </SubSection>

                    <SubSection title="Ejemplo completo — Panel de disponibilidad">
                        <div className="docs-note">
                            <span className="icon" data-icon="info" />
                            <Text size="small">
                                Caso de uso real: panel lateral con encabezado, secciones colapsables,
                                lista de operadores con <code>Avatar</code> + <code>Status</code>, y lista de
                                unidades. La flecha permanece fija aunque el contenido sea largo.
                            </Text>
                        </div>
                        {(() => {
                            const OPERADORES = [
                                { nombre: "Benjamín Hernández García",  estatus: "VACACIONES" },
                                { nombre: "Lucio Antonio Reyes Luna",   estatus: "VACACIONES" },
                                { nombre: "Jorge Armando de Luna Oce.", estatus: "INCAPACIDAD" },
                            ];
                            const UNIDADES = [
                                { id: "29",  desc: "HIACE",   estado: "NO DISPONIBLE",    severity: "error"   },
                                { id: "50",  desc: "YARIS",   estado: "NO DISPONIBLE",    severity: "error"   },
                                { id: "55",  desc: "SPRINTER",estado: "TALLER LARGO PLAZO",severity: "warning" },
                                { id: "76",  desc: "SPRINTER",estado: "TALLER LARGO PLAZO",severity: "warning" },
                                { id: "98",  desc: "ORION VW",estado: "NO DISPONIBLE",    severity: "error"   },
                            ];
                            const FullDemo = () => (
                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <SideDrawer defaultCollapsed={false} width={320}>
                                        <aside style={{ display: "flex", flexDirection: "column" }}>
                                            {/* Header */}
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 8,
                                                padding: "14px 16px 12px",
                                                borderBottom: "1px solid var(--gray-200)",
                                            }}>
                                                <span className="icon" data-icon="info" style={{ fontSize: 18, color: "var(--blue-500)" }} />
                                                <Text size="small" weight={600}>Disponibilidad</Text>
                                                <Text size="extra-small" color="var(--gray-dark-400)" style={{ marginLeft: "auto" }}>
                                                    {new Date().toISOString().slice(0, 10)}
                                                </Text>
                                            </div>

                                            {/* Operadores */}
                                            <Collapse icon="person_off" title={`Operadores no disponibles (${OPERADORES.length})`} defaultOpen>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0 8px" }}>
                                                    {OPERADORES.map((op, i) => (
                                                        <div key={i} style={{
                                                            display: "flex", alignItems: "center", gap: 8,
                                                            padding: "6px 8px", borderRadius: 6,
                                                            background: "var(--gray-50, rgba(0,0,0,0.02))",
                                                            border: "1px solid var(--gray-100, rgba(0,0,0,0.05))",
                                                        }}>
                                                            <Avatar label={op.nombre} size="small" />
                                                            <Text size="extra-small" weight={600} style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {op.nombre}
                                                            </Text>
                                                            <Status severity="warning" style={{ flexShrink: 0, fontSize: 9, padding: "2px 6px" }}>
                                                                {op.estatus}
                                                            </Status>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Collapse>

                                            {/* Unidades */}
                                            <Collapse icon="directions_bus" title={`Unidades no disponibles (${UNIDADES.length})`} defaultOpen>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0 8px" }}>
                                                    {UNIDADES.map((u, i) => (
                                                        <div key={i} style={{
                                                            display: "flex", alignItems: "center", gap: 8,
                                                            padding: "6px 8px", borderRadius: 6,
                                                            background: "var(--gray-50, rgba(0,0,0,0.02))",
                                                            border: "1px solid var(--gray-100, rgba(0,0,0,0.05))",
                                                        }}>
                                                            <div style={{
                                                                width: 30, height: 30, borderRadius: "50%",
                                                                background: "var(--red-100)", display: "flex",
                                                                alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                            }}>
                                                                <span className="icon" data-icon="directions_bus" style={{ fontSize: 16, color: "var(--red-500)" }} />
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Text size="extra-small" weight={600}>U-{u.id}</Text>
                                                                <Text size="extra-small" color="var(--gray-dark-400)" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {u.desc}
                                                                </Text>
                                                            </div>
                                                            <Status severity={u.severity} style={{ flexShrink: 0, fontSize: 9, padding: "2px 6px" }}>
                                                                {u.estado}
                                                            </Status>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Collapse>
                                        </aside>
                                    </SideDrawer>
                                </div>
                            );
                            return <FullDemo />;
                        })()}
                        <pre className="docs-code"><code>{`<SideDrawer defaultCollapsed={false} width={320}>
  <aside>
    {/* ── Header ── */}
    <div className="ps-aside-header">
      <span className="icon" data-icon="info" />
      <Text size="small" weight={600}>Disponibilidad</Text>
      <Text size="extra-small" color="var(--gray-dark-400)" className="ps-aside-date">
        {new Date().toISOString().slice(0, 10)}
      </Text>
    </div>

    {/* ── Sección colapsable: operadores ── */}
    <Collapse icon="person_off" title="Operadores no disponibles (3)" defaultOpen>
      {operadores.map((op, i) => (
        <div key={i} className="ps-no-disp-item">
          <Avatar label={op.NOMBRE} size="small" />
          <Text size="extra-small" weight={600}>{op.NOMBRE}</Text>
          <Status severity="warning">{op.ESTATUS}</Status>
        </div>
      ))}
    </Collapse>

    {/* ── Sección colapsable: unidades ── */}
    <Collapse icon="directions_bus" title="Unidades no disponibles (5)" defaultOpen>
      {unidades.map((u, i) => (
        <div key={i} className="ps-no-disp-item">
          <div className="ps-unit-badge">
            <span className="icon" data-icon="directions_bus" />
          </div>
          <div className="ps-no-disp-info">
            <Text size="extra-small" weight={600}>U-{u.ID}</Text>
            <Text size="extra-small" color="var(--gray-dark-400)">{u.DESCRIPCION}</Text>
          </div>
          <Status severity={u.U_DISPONIBLE === 'NO DISPONIBLE' ? 'error' : 'warning'}>
            {u.U_DISPONIBLE}
          </Status>
        </div>
      ))}
    </Collapse>
  </aside>
</SideDrawer>`.trim()}</code></pre>
                    </SubSection>
                </Section>

                {/* ════════════════════════════════════════════ KANBAN PANEL ══ */}
                <Section id="kanban-panel" badge="molecule" title="KanbanPanel"
                    description="Panel tipo kanban con drag-and-drop nativo para mover tarjetas dentro de una columna o entre columnas. Sin dependencias externas: usa la HTML Drag and Drop API. Mantiene la estética v2 y usa tokens de color globales.">

                    <PropsTable rows={[
                        { name: "title",        type: "string",   desc: "Título del tablero mostrado en el encabezado superior." },
                        { name: "modules",      type: "array",    desc: "Columnas del kanban. Formato: [{ id, title, items: [{ id, title, description?, tag? }] }]." },
                        { name: "onChange",     type: "function", desc: "Callback al soltar una tarjeta. Recibe (nextModules, movement). movement = { item, fromModuleId, toModuleId, toIndex }." },
                        { name: "columnHeight", type: "number",   default: "420",                       desc: "Altura máxima en px del cuerpo de cada columna. Activa scroll vertical si el contenido supera ese límite." },
                        { name: "emptyMessage", type: "string",   default: '"Suelta una tarjeta aqui"', desc: "Texto que se muestra en columnas sin tarjetas." },
                        { name: "disabled",     type: "boolean",  default: "false",                     desc: "Deshabilita el arrastre de todas las tarjetas." },
                        { name: "showHistory",  type: "boolean",  default: "false",                     desc: "Muestra un panel de historial bajo el tablero con los últimos movimientos." },
                        { name: "showReset",    type: "boolean",  default: "false",                     desc: "Muestra un botón Restablecer tablero en el encabezado que vuelve al estado inicial de modules." },
                        { name: "maxHistory",   type: "number",   default: "5",                         desc: "Número máximo de entradas visibles en el historial. Solo aplica si showHistory=true." },
                        { name: "resetLabel",   type: "string",   default: '"Restablecer tablero"',     desc: "Texto del botón de restablecimiento. Solo aplica si showReset=true." },
                        { name: "className",    type: "string",   default: '""',                        desc: "Clase CSS extra para el contenedor raíz." },
                    ]} />

                    <SubSection title="Ejemplo completo — Solicitudes de mantenimiento">
                        <div className="docs-note">
                            <span className="icon" data-icon="drag_indicator" />
                            <Text size="small">
                                Arrastra cualquier tarjeta entre columnas o dentro de la misma columna.
                                El historial registra cada movimiento. Usa <strong>Restablecer tablero</strong> para volver al estado inicial.
                            </Text>
                        </div>
                        <KanbanPanelFullDemo />
                    </SubSection>

                    <Preview label="Uso básico" code={`import KanbanPanel from "@/components/moleculesv2/KanbanPanel/KanbanPanel";

const [modules, setModules] = useState([
    {
        id: "pendiente",
        title: "Pendiente",
        items: [
            { id: "t-1", title: "Cambio de aceite U-29", description: "HIACE · venc. hoy", tag: "Urgente" },
            { id: "t-2", title: "Revisión de frenos U-50" },
        ],
    },
    { id: "en-proceso", title: "En proceso", items: [] },
    { id: "finalizado",  title: "Finalizado",  items: [] },
]);

// Mínimo
<KanbanPanel
    title="Solicitudes de mantenimiento"
    modules={modules}
    onChange={(nextModules) => setModules(nextModules)}
    columnHeight={320}
/>

// Con historial y botón de restablecimiento activados
<KanbanPanel
    title="Solicitudes de mantenimiento"
    modules={modules}
    onChange={(nextModules) => setModules(nextModules)}
    columnHeight={320}
    showHistory          // muestra historial bajo el tablero
    showReset            // muestra botón "Restablecer tablero"
    maxHistory={8}       // hasta 8 entradas en el historial
    resetLabel="Reiniciar"  // texto personalizado del botón
/>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>
                </Section>

                {/* ═══════════════════════════════ INFO TOOLTIP ═══════════════════════════════ */}
                <Section id="info-tooltip" badge="molecule" title="InfoTooltip"
                    description="Popover que se activa al hacer hover sobre `trigger` (igual dinámica que Atomsv2/Tooltip) pero se renderiza con createPortal hacia document.body, por lo que nunca queda detrás de cards u otros elementos hermanos. Se anima en dos etapas: primero crece hacia la derecha revelando un header (icono + título), y luego crece hacia abajo revelando el contenido (children).">

                    <PropsTable rows={[
                        { name: "trigger",      type: "ReactNode", desc: "Elemento sobre el que el usuario hace hover para abrir el popover (ícono, texto, botón, etc.)." },
                        { name: "icon",         type: "string",    desc: "Nombre de Material Icon mostrado en el header del popover, junto al título." },
                        { name: "title",        type: "string",    desc: "Título del header del popover. Se revela en la primera etapa de la animación (crecimiento horizontal)." },
                        { name: "children",     type: "ReactNode", desc: "Contenido del popover. Se revela en la segunda etapa (crecimiento vertical)." },
                        { name: "className",    type: "string",   default: '""', desc: "Clase CSS extra para el contenedor del trigger." },
                        { name: "triggerStyle", type: "object",   desc: "Estilos inline aplicados al contenedor del trigger." },
                    ]} />

                    <Preview
                        label="Hover sobre el ícono"
                        code={`import InfoTooltip from "@/components/molecules/InfoTooltip/InfoTooltip";

<InfoTooltip
  icon="build"
  title="SERVICIO TIPO A DE 20,000 KM"
  trigger={<span className="icon" data-icon="info" />}
>
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span className="icon" data-icon="check_circle" />
    <span>Cambio de aceite</span>
  </div>
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span className="icon" data-icon="check_circle" />
    <span>Filtro de aceite</span>
  </div>
</InfoTooltip>`}>
                        <InfoTooltip
                            icon="build"
                            title="SERVICIO TIPO A DE 20,000 KM"
                            trigger={<span className="icon" data-icon="info" style={{ fontSize: 22, color: "var(--blue-600)", cursor: "pointer" }} />}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span className="icon" data-icon="check_circle" style={{ color: "var(--green-500)" }} />
                                <Text size="small">Cambio de aceite</Text>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span className="icon" data-icon="check_circle" style={{ color: "var(--green-500)" }} />
                                <Text size="small">Filtro de aceite</Text>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span className="icon" data-icon="check_circle" style={{ color: "var(--green-500)" }} />
                                <Text size="small">Filtro de aire</Text>
                            </div>
                        </InfoTooltip>
                    </Preview>
                </Section>

                {/* ════════════════════════════════════════════ CARDDASHBOARD ═ */}
                <Section id="card-dashboard" badge="molecule" title="CardDashboard"
                    description="Tarjeta contenedora para el Dashboard. Muestra un título con ícono opcional en la cabecera, un área de contenido y un slot de footer.">

                    <PropsTable rows={[
                        { name: "title",     type: "string",   default: '"Titulo"', desc: "Texto que aparece en la cabecera de la tarjeta." },
                        { name: "icon",      type: "string",   default: '"info"',   desc: "Nombre de Material Icon mostrado a la derecha del título." },
                        { name: "iconColor", type: "string",   desc: "Color CSS aplicado al ícono de la cabecera." },
                        { name: "iconSize",  type: "string",   default: '"15px"',   desc: "Tamaño de fuente (font-size) del ícono de la cabecera." },
                        { name: "children",  type: "ReactNode",desc: "Contenido principal que se renderiza dentro de la tarjeta." },
                        { name: "footer",    type: "ReactNode",desc: "Contenido que se renderiza en el pie de la tarjeta." },
                        { name: "style",     type: "object",   desc: "Estilos inline adicionales aplicados al contenedor raíz." },
                    ]} />

                    <Preview label="Copiar y pegar" code={`import CardDashboard from "@/components/moleculesv2/DashboardCard/CardDashboard";

<CardDashboard
  title="Título de ejemplo"
  icon="info"
  iconColor="var(--blue-500)"
  footer={<Text size="small" color="var(--gray-dark-500)">Pie de tarjeta</Text>}
>
  <Text>Contenido de la tarjeta</Text>
</CardDashboard>`}>
                        <CardDashboard
                            title="Título de ejemplo"
                            icon="info"
                            iconColor="var(--blue-500)"
                            footer={<Text size="small" color="var(--gray-dark-500)">Pie de tarjeta</Text>}
                        >
                            <Text>Contenido de la tarjeta</Text>
                        </CardDashboard>
                    </Preview>
                </Section>

                {/* ════════════════════════════════════════════ DROPDOWNBUTTON ═ */}
                <Section id="dropdown-button" badge="molecule" title="DropdownButton"
                    description="Botón primario que al pulsarse despliega un menú flotante con acciones configurables. Cada opción acepta icono (Material Icon), etiqueta y función onClick. Se cierra automáticamente al hacer clic fuera.">

                    <PropsTable rows={[
                        { name: "label",    type: "string",   default: '"Nuevo"', desc: 'Texto del botón.' },
                        { name: "icon",     type: "string",   default: '"add"',   desc: 'Material Icon que aparece a la izquierda del label en el botón.' },
                        { name: "items",    type: "array",    default: "[]",      desc: 'Arreglo de opciones. Cada objeto: { icono: string, title: string, onClick: () => void }.' },
                        { name: "disabled", type: "boolean",  default: "false",   desc: 'Deshabilita el botón y evita que se abra el menú.' },
                        { name: "className",type: "string",   default: '""',      desc: 'Clase CSS adicional para el contenedor raíz.' },
                    ]} />

                    <Preview label="Demo" code={`import DropdownButton from "@/components/moleculesv2/DropdownButton/DropdownButton";

const items = [
  { icono: "crop_square",  title: "Área",     onClick: () => console.log("Área") },
  { icono: "folder",       title: "Proyecto", onClick: () => console.log("Proyecto") },
  { icono: "sprint",       title: "Sprint",   onClick: () => console.log("Sprint") },
  { icono: "add_task",     title: "Tarea",    onClick: () => console.log("Tarea") },
];

<DropdownButton label="Nuevo" icon="add" items={items} />`}>
                        <DropdownButton
                            label="Nuevo"
                            icon="add"
                            items={[
                                { icono: "crop_square",  title: "Área",     onClick: () => {} },
                                { icono: "folder",       title: "Proyecto", onClick: () => {} },
                                { icono: "sprint",       title: "Sprint",   onClick: () => {} },
                                { icono: "add_task",     title: "Tarea",    onClick: () => {} },
                            ]}
                        />
                    </Preview>
                </Section>


                <Section id="side-panel" badge="organism" title="SidePanel"
                    description="Panel lateral deslizante (drawer) renderizado en un portal sobre el body. Soporta animación de entrada/salida, posicionamiento izquierda/derecha, encabezado con icono y subtítulo, slot de footer, cierre con Escape o clic en el overlay.">

                    <PropsTable rows={[
                        { name: "open",        type: "boolean",   desc: "Controla si el panel está visible." },
                        { name: "onClose",     type: "function",  desc: "Callback invocado cuando el usuario cierra el panel." },
                        { name: "title",       type: "string",    desc: "Título principal del encabezado." },
                        { name: "subtitle",    type: "string",    desc: "Subtítulo secundario debajo del título." },
                        { name: "icon",        type: "string",    desc: "Nombre de Material Icon para el encabezado." },
                        { name: "headerClass", type: "string",    desc: "Clase CSS extra para el contenedor del encabezado." },
                        { name: "footer",      type: "ReactNode", desc: "Contenido del pie del panel." },
                        { name: "width",       type: "string",    default: "\"360px\"", desc: "Ancho del panel. Máximo 95vw." },
                        { name: "placement",   type: "string",    default: "\"right\"", desc: "Posición: \"right\" | \"left\"." },
                        { name: "children",    type: "ReactNode", desc: "Contenido del cuerpo del panel." },
                        { name: "tabToClose",  type: "boolean",   default: "true", desc: "Si es true, permite cerrar el panel al hacer clic en el overlay (background). Si es false, el clic en el overlay queda inactivo; Escape y el botón de cerrar siguen funcionando." },
                    ]} />

                    <SubSection title="Demo interactivo">
                        <SidePanelDemo />
                    </SubSection>

                    <Preview label="Uso básico" code={`const [open, setOpen] = useState(false);

<Button onClick={() => setOpen(true)}>Abrir panel</Button>

<SidePanel
  open={open}
  onClose={() => setOpen(false)}
  title="Detalle"
  subtitle="Información adicional"
  icon="info"
  footer={<Button onClick={() => setOpen(false)}>Cerrar</Button>}
>
  <p>Contenido del panel</p>
</SidePanel>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>

                    <Preview label="tabToClose=false (el clic en el overlay no cierra)" code={`<SidePanel
  open={open}
  onClose={() => setOpen(false)}
  title="No cerrable por clic en el overlay"
  tabToClose={false}
>
  <p>Escape y el botón de cerrar siguen funcionando; el overlay ya no.</p>
</SidePanel>`}>
                        <div style={{ height: 1, display: "none" }} />
                    </Preview>
                </Section>

                {/* ════════════════════════════════════════════ SIDEMENU ══ */}
                <Section id="side-menu" badge="organism" title="SideMenu"
                    description="Menú lateral de navegación principal de la aplicación. Carga los permisos del usuario desde la API, agrupa enlaces en padre/hijos colapsables, soporta modo colapsado (icono-only) con expansión al hover en tablet, y modo overlay en móvil.">

                    <PropsTable rows={[
                        { name: "mobileOpen",    type: "boolean",  default: "false", desc: "Muestra el menú en modo overlay (móvil)." },
                        { name: "onMobileClose", type: "function", desc: "Callback para cerrar el overlay móvil." },
                    ]} />

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small">
                            <strong>SideMenu</strong> obtiene su estructura de navegación mediante <code>IntegracionSideMenu.getMenu()</code>,
                            que retorna un array de grupos padre con sus rutas hijas e iconos.
                            La navegación se realiza con <code>useNavigate</code> de React Router.
                        </Text>
                    </div>

                    <SubSection title="Estructura de datos de menú">
                        <pre className="docs-code"><code>{`// Formato retornado por IntegracionSideMenu.getMenu()
[
  {
    PADRE: "Planeación",
    ICONO_PADRE: "calendar_month",
    hijos: [
      { NOMBRE: "Programación", RUTA: "/planeacion/programacion", ICONO: "event_note" },
    ],
  },
]`}</code></pre>
                    </SubSection>

                    <SubSection title="Comportamiento por breakpoint">
                        <Preview label="Estados según pantalla" code={`// > 1024px  → siempre visible, botón para colapsar/expandir
// 768–1023px → colapsado por defecto, se expande al hover
// < 768px    → oculto, se abre con mobileOpen=true (overlay)`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {[
                                    { icon: "desktop_windows", label: "≥ 1024px — expandido, colapsable por botón" },
                                    { icon: "tablet",          label: "768–1023px — colapsado (íconos), hover para expandir" },
                                    { icon: "smartphone",      label: "< 768px — oculto, abre como overlay" },
                                ].map(r => (
                                    <div key={r.icon} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span className="icon" data-icon={r.icon} style={{ color: "var(--blue-500)" }} />
                                        <Text size="small">{r.label}</Text>
                                    </div>
                                ))}
                            </div>
                        </Preview>
                    </SubSection>
                </Section>

                {/* ════════════════════════════════════════════ AUTHGRID ══ */}
                <Section id="auth-grid" badge="organism" title="AuthGrid"
                    description="Layout principal de las vistas autenticadas. Compone SideMenu + Header + área de contenido en un grid responsivo. Es el wrapper de todas las rutas protegidas de la aplicación.">

                    <PropsTable rows={[
                        { name: "children", type: "ReactNode", desc: "Contenido de la vista que se renderiza en el área principal del grid." },
                    ]} />

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small">
                            <strong>AuthGrid</strong> gestiona internamente el estado <code>mobileMenuOpen</code> y lo pasa a <code>SideMenu</code> y <code>Header</code>.
                            Todas las rutas protegidas deben estar envueltas en este componente.
                        </Text>
                    </div>

                    <Preview label="Uso en el router" code={`// Planeacion.jsx (o tu archivo de rutas)
import AuthGrid from "../Components/Organisms/AuthGrid/AuthGrid";

<Route element={<AuthGrid />}>
  <Route path="/dashboard"  element={<Dashboard />} />
  <Route path="/reportes"   element={<Reportes />}  />
</Route>

// O bien como wrapper directo:
<AuthGrid>
  <MiVista />
</AuthGrid>`}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <Text size="small" color="var(--gray-dark-500)">Estructura del grid:</Text>
                            {[
                                { icon: "menu",     label: "SideMenu — columna izquierda, navegación principal" },
                                { icon: "web_asset",label: "Header — fila superior, acciones y usuario" },
                                { icon: "crop_free",label: "children — área de contenido principal" },
                            ].map(r => (
                                <div key={r.icon} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className="icon" data-icon={r.icon} style={{ color: "var(--blue-500)" }} />
                                    <Text size="small">{r.label}</Text>
                                </div>
                            ))}
                        </div>
                    </Preview>
                </Section>

                {/* ══════════════════════════════════════════ GANTTCHART ══ */}
                <Section id="gantt-chart" badge="organism" title="GanttChart"
                    description="Diagrama de Gantt genérico y reutilizable. Recibe un array de filas (rows[]) con el formato propio del componente; quien lo usa es responsable de transformar su data a ese formato. Incluye selector de escala Día / Semana / Mes / Trimestre, columna izquierda congelada y línea de hoy.">

                    {/* ── Props ── */}
                    <PropsTable rows={[
                        { name: "rows",    type: "array",    default: "[]",         desc: "Lista de filas a renderizar. Ver la tabla de forma de cada fila más abajo." },
                        { name: "onClick", type: "function", default: "undefined",  desc: "Callback que recibe el objeto row completo al hacer clic en una fila. Solo se activa en filas con isGroup: false." },
                    ]} />

                    <SubSection title="Forma de cada objeto dentro de rows">
                        <PropsTable rows={[
                            { name: "id",      type: "string | number", desc: "Clave única de la fila." },
                            { name: "label",   type: "string",          desc: "Texto mostrado en la columna izquierda." },
                            { name: "start",   type: "string",          desc: "'YYYY-MM-DD'. Fecha de inicio de la barra. Omitir en filas de grupo." },
                            { name: "end",     type: "string",          desc: "'YYYY-MM-DD'. Fecha de fin de la barra. Omitir en filas de grupo." },
                            { name: "color",   type: "string",          default: "'#94a3b8'", desc: "Color CSS de la barra (hex, rgb, var…)." },
                            { name: "badge",   type: "ReactNode",       desc: "Elemento renderizado en la celda de estado (ej. <Status>)." },
                            { name: "icon",    type: "string",          desc: "Nombre de Material Icon mostrado antes del label." },
                            { name: "indent",  type: "0 | 1 | 2",      default: "0",          desc: "Nivel de sangría: 0=raíz, 1=nivel medio, 2=hoja." },
                            { name: "isGroup", type: "boolean",         default: "false",      desc: "true → fila de agrupador (sin barra, fondo diferente)." },
                            { name: "tooltip", type: "string",          desc: "Texto del tooltip de la barra. Por defecto usa label + fechas." },
                        ]} />
                    </SubSection>

                    {/* ── Demo interactivo ── */}
                    <SubSection title="Demo interactivo — haz clic en cualquier tarea">
                        <GanttChartDemo />
                    </SubSection>

                    {/* ── Ejemplo copy-paste ── */}
                    <Preview label="Ejemplo completo — copia y pega" code={`import GanttChart from "../organismsv2/GanttChart/GanttChart";
import Status     from "../Atomsv2/Status/Status";

const rows = [

  // ── ÁREA ─────────────────────────────────────────────────────────────────────
  // isGroup: true  →  solo aparece en la columna izquierda, sin barra en el diagrama
  // indent: 0      →  sin sangría (nivel raíz)
  {
    id:      'area-1',
    label:   'Desarrollo Web',
    icon:    'folder',
    isGroup: true,
    indent:  0,
  },

  // ── PROYECTO ─────────────────────────────────────────────────────────────────
  // indent: 1  →  sangría de un nivel (debajo del área)
  {
    id:      'proy-1',
    label:   'Portal de clientes',
    icon:    'article',
    isGroup: true,
    indent:  1,
  },

  // ── TAREAS ───────────────────────────────────────────────────────────────────
  // start / end  →  posición y ancho de la barra en el diagrama  (formato YYYY-MM-DD)
  // color        →  color de la barra
  // badge        →  elemento que aparece en la celda "Estado" de la columna izquierda
  // indent: 2    →  sangría de dos niveles (hoja del árbol)
  {
    id:      1,
    label:   'Wireframes',
    start:   '2026-08-01',
    end:     '2026-08-10',
    color:   '#16a34a',
    badge:   <Status severity="success">Terminado</Status>,
    icon:    'task_alt',
    indent:  2,
  },
  {
    id:      2,
    label:   'Maquetación',
    start:   '2026-08-11',
    end:     '2026-08-25',
    color:   '#2563eb',
    badge:   <Status severity="primary">En proceso</Status>,
    icon:    'task_alt',
    indent:  2,
    // tooltip  →  texto que aparece al hacer hover sobre la barra
    tooltip: 'Maquetación · En proceso · 2026-08-11 → 2026-08-25',
  },
  {
    id:      3,
    label:   'Pruebas QA',
    start:   '2026-08-26',
    end:     '2026-09-05',
    color:   '#94a3b8',
    badge:   <Status severity="warning">Pendiente</Status>,
    icon:    'task_alt',
    indent:  2,
  },
];

export default function MiVista() {
  const [tarea, setTarea] = React.useState(null);

  return (
    <>
      <GanttChart
        rows={rows}
        // onClick recibe el objeto row completo; solo se activa en tareas (no grupos)
        onClick={(row) => setTarea(row)}
      />

      {tarea && (
        <Modal title={tarea.label} onClose={() => setTarea(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Estado</span>
              {tarea.badge}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Inicio</span>
              <span>{tarea.start}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Fin</span>
              <span>{tarea.end}</span>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}`} />

                    {/* ── Escalas ── */}
                    <SubSection title="Escalas disponibles">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[
                                { icon: 'today',          label: 'Día — 40 px/día. Columna por cada día con su inicial (L/M/M/…).' },
                                { icon: 'view_week',      label: 'Semana — 14 px/día. Etiqueta al inicio de cada semana.' },
                                { icon: 'calendar_month', label: 'Mes — 5 px/día. Una columna por mes.' },
                                { icon: 'date_range',     label: 'Trimestre — 2 px/día. Columnas Q1/Q2/Q3/Q4.' },
                            ].map(r => (
                                <div key={r.icon} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className="icon" data-icon={r.icon} style={{ color: 'var(--blue-500)', fontSize: 18 }} />
                                    <Text size="small">{r.label}</Text>
                                </div>
                            ))}
                        </div>
                    </SubSection>

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small">
                            El componente es <strong>agnóstico al dominio</strong>: no sabe nada de proyectos ni tareas.
                            La transformación de tu data al formato <code>rows[]</code> la haces tú antes de pasarla.
                            Ver <code>GantView.jsx</code> como ejemplo de adaptador para el módulo de Proyectos.
                        </Text>
                    </div>
                </Section>

                {/* ═══════════════════════════════════════════════ UTILS */}

                <Section id="use-user-data" badge="util" title="useUserData"
                    description="Custom hook que lee el token JWT del sessionStorage, lo decodifica y devuelve los datos del usuario junto con un flag sesionActive.">

                    <SubSection title="Retorno">
                        <PropsTable rows={[
                            { name: "(retorno)",    type: "object | null", default: "null",  desc: "null si no hay token o es inválido. En caso contrario, devuelve el payload del JWT con sesionActive añadido." },
                            { name: "sesionActive", type: "boolean",       default: "false", desc: "true si el campo exp del token es mayor que el timestamp actual (sesión vigente)." },
                            { name: "permisos",     type: "array",         default: "—",     desc: "Array de permisos del payload. Cada item tiene al menos RUTA. Usado por RutasProtegidas." },
                        ]} />
                    </SubSection>

                    <SubSection title="Exportaciones">
                        <PropsTable rows={[
                            { name: "useUserData",      type: "hook (default)",    desc: "Hook principal. Llama internamente a desencriptarToken." },
                            { name: "desencriptarToken",type: "function (named)",  desc: "Función pura. Recibe un token string y devuelve el payload decodificado + sesionActive. Útil fuera de componentes." },
                        ]} />
                    </SubSection>

                    <Preview label="Uso básico" code={`import useUserData from "../CustomHooks/getUserData";

const MiComponente = () => {
    const userData = useUserData();

    if (!userData?.sesionActive) return <p>Sesión expirada</p>;

    return <p>Bienvenido, {userData.nombre}</p>;
};`} />

                    <Preview label="Decodificar un token manualmente" code={`import { desencriptarToken } from "../CustomHooks/getUserData";

const payload = desencriptarToken(miToken);
console.log(payload.sesionActive); // true | false`} />

                    <div className="docs-note">
                        <span className="icon" data-icon="security" />
                        <Text size="small">
                            El token se lee de <code>sessionStorage</code> (no localStorage) para que se limpie automáticamente al cerrar el navegador.
                        </Text>
                    </div>
                </Section>

                <Section id="rutas-protegidas" badge="util" title="RutasProtegidas"
                    description="Componente guardián de rutas. Verifica que el usuario tenga un permiso cuya RUTA coincida con el pathname actual. Si no, redirige.">

                    <PropsTable rows={[
                        { name: "redirectTo", type: "string", default: "\'/ 404\'", desc: "Ruta a la que redirige cuando el usuario no tiene acceso al pathname actual." },
                    ]} />

                    <Preview label="En el router" code={`import RutasProtegidas from "../utils/Router/RutasProtegidas";

<Route element={<RutasProtegidas redirectTo="/sin-acceso" />}>
    <Route path="/dashboard"  element={<Dashboard />} />
    <Route path="/reportes"   element={<Reportes />}  />
</Route>`} />

                    <SubSection title="Lógica interna">
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {[
                                { icon: "key",            text: "Llama a useUserData() para obtener el payload del JWT." },
                                { icon: "find_in_page",   text: "Compara pathname actual con userData.permisos[].RUTA." },
                                { icon: "check_circle",   text: "Si hay coincidencia → renderiza <Outlet /> (la ruta hija)." },
                                { icon: "block",          text: "Si no hay coincidencia → <Navigate to={redirectTo} replace />." },
                            ].map(r => (
                                <div key={r.icon} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className="icon" data-icon={r.icon} style={{ color: "var(--blue-500)", fontSize: 18 }} />
                                    <Text size="small">{r.text}</Text>
                                </div>
                            ))}
                        </div>
                    </SubSection>
                </Section>

                <Section id="api-interceptor" badge="util" title="api (interceptor)"
                    description="Configura los interceptores globales de Axios. Agrega el token JWT en cada request y redirige al login ante respuestas 401.">

                    <SubSection title="Interceptor de request">
                        <PropsTable rows={[
                            { name: "Authorization", type: "header", desc: "Agrega Bearer {token} a todos los requests si existe un token en localStorage." },
                        ]} />
                    </SubSection>

                    <SubSection title="Interceptor de response">
                        <PropsTable rows={[
                            { name: "401 Unauthorized", type: "status", desc: "Redirige automáticamente a /login cuando el servidor responde con 401." },
                        ]} />
                    </SubSection>

                    <Preview label="Inicializar al arrancar la app" code={`// main.jsx
import { api } from "./utils/Integration/interseptor";

api(); // registra los interceptores una sola vez

ReactDOM.createRoot(document.getElementById("root")).render(
    <BrowserRouter>
        <App />
    </BrowserRouter>
);`} />

                    <Preview label="Uso con axios después de inicializar" code={`import axios from "axios";

// No hace falta configurar headers manualmente;
// el interceptor los agrega automáticamente.
const res = await axios.get("/api/servicios");
console.log(res.data);`} />

                    <div className="docs-note">
                        <span className="icon" data-icon="warning" style={{ color: "var(--orange-500)" }} />
                        <Text size="small">
                            Llamar a <code>api()</code> más de una vez registra los interceptores múltiples veces. Asegúrate de invocarlo una sola vez en el punto de entrada (<code>main.jsx</code>).
                        </Text>
                    </div>
                </Section>

                {/* ═══════════════════════════════ SISTEMA DE RUTEO ═══════════════════════════════ */}
                <Section id="sistema-ruteo" badge="util" title="Sistema de Ruteo"
                    description="Cómo está organizado el sistema de rutas de la aplicación: estructura de archivos, reglas de paths, protección por permisos y cómo agregar un módulo nuevo.">

                    <div className="docs-note">
                        <span className="icon" data-icon="info" />
                        <Text size="small">
                            El sistema usa <strong>React Router v6</strong> con un único <code>&lt;Routes&gt;</code> por nivel.
                            Nunca se renderizan dos <code>&lt;Routes&gt;</code> en paralelo: uno despacha y cada módulo
                            resuelve sus propias rutas de forma independiente.
                        </Text>
                    </div>

                    <SubSection title="Árbol de archivos">
                        <pre className="docs-code"><code>{`src/
  main.jsx                         ← monta <BrowserRouter> + api()
  Rutas/
    MainRouter.jsx                 ← lee sessionStorage, decide AuthApp o GuestApp
    AuthApp.jsx                    ← un único <Routes> que despacha por prefijo
    GuestApp.jsx                   ← rutas públicas (login, 404)
    Planeacion.jsx                 ← módulo: rutas relativas + RutasProtegidas
    Operacion.jsx                  ← módulo: rutas relativas + RutasProtegidas
  utils/Router/
    RutasProtegidas.jsx            ← layout route que verifica userData.permisos`}</code></pre>
                    </SubSection>

                    <SubSection title="Flujo completo de una petición">
                        <pre className="docs-code"><code>{`URL: /planeacion/transferencias

1. MainRouter.jsx
   sessionStorage.authMonitor === "true"  →  monta <AuthApp />

2. AuthApp.jsx
   pathname ≠ "/404" y ≠ "/docs"  →  renderiza <AuthGrid>
   <Routes>
     <Route path="/planeacion/*"  element={<Planeacion />} />  ← COINCIDE
     <Route path="/operacion/*"   element={<Operacion />}  />
     <Route path="*" element={<Navigate to="/404" />}      />
   </Routes>

3. Planeacion.jsx  (el sufijo que llega es "transferencias")
   <Routes>
     <Route element={<RutasProtegidas />}>     ← layout route sin path
       <Route path="transferencias" ... />     ← COINCIDE con el sufijo
     </Route>
     <Route path="*" element={<Navigate to="/404" />} />
   </Routes>

4. RutasProtegidas.jsx
   pathname completo = "/planeacion/transferencias"
   userData.permisos.some(p => p.RUTA === pathname)  →  true
   →  <Outlet />  →  renderiza <Transferencias />`}</code></pre>
                    </SubSection>

                    <SubSection title="AuthApp.jsx — despachador central">
                        <pre className="docs-code"><code>{`// src/Rutas/AuthApp.jsx
import { useLocation, Routes, Route, Navigate } from "react-router-dom";
import AuthGrid  from "../Components/Organisms/AuthGrid/AuthGrid";
import Planeacion from "./Planeacion";
import Operacion  from "./Operacion";
import Docs       from "../Views/auth/Docs/Docs";
import NotFound   from "../Views/guest/NotFound/NotFound";

const AuthApp = () => {
    const { pathname } = useLocation();

    // Páginas full-screen (sin AuthGrid)
    if (pathname === "/404")  return <NotFound />;
    if (pathname === "/docs") return <Docs />;

    return (
        <AuthGrid>
            {/*
             * Un único <Routes> despacha a cada módulo por prefijo.
             * El /* final es obligatorio: le dice a React Router
             * que el módulo manejará el resto de la URL.
             */}
            <Routes>
                <Route path="/planeacion/*" element={<Planeacion />} />
                <Route path="/operacion/*"  element={<Operacion />}  />
                <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
        </AuthGrid>
    );
};`}</code></pre>
                    </SubSection>

                    <SubSection title="Archivo de módulo — Planeacion.jsx">
                        <pre className="docs-code"><code>{`// src/Rutas/Planeacion.jsx
import { Route, Routes, Navigate } from "react-router-dom";
import RutasProtegidas         from "../utils/Router/RutasProtegidas";
import ProgramacionDeServicios from "../Views/auth/Planeacion/ProgramacionDeServicios/...";
import Excepciones             from "../Views/auth/Planeacion/Excepciones/...";
// ...demás vistas

const Planeacion = () => (
    <Routes>
        {/*
         * Layout route sin path: siempre se evalúa,
         * pero solo renderiza su Outlet cuando un hijo coincide.
         * RutasProtegidas verifica los permisos del usuario.
         */}
        <Route element={<RutasProtegidas redirectTo="/404" />}>

            {/*
             * REGLA: paths relativos (sin /planeacion/ al inicio).
             * AuthApp ya consumió ese prefijo. Lo que llega aquí
             * es solo el segmento final: "programacion-servicios",
             * "transferencias", etc.
             */}
            <Route path="programacion-servicios" element={<ProgramacionDeServicios />} />
            <Route path="excepciones"            element={<Excepciones />} />
            <Route path="transferencias"         element={<Transferencias />} />
            <Route path="programacion-masiva"    element={<ProgramacionMasiva />} />
        </Route>

        {/*
         * Catch-all HERMANO del layout route (no dentro de él).
         * Se activa solo cuando ningún path relativo coincide.
         * Ej: /planeacion/ruta-que-no-existe → 404
         */}
        <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
);`}</code></pre>
                    </SubSection>

                    <SubSection title="RutasProtegidas.jsx — protección por permisos">
                        <pre className="docs-code"><code>{`// src/utils/Router/RutasProtegidas.jsx
import { Navigate, Outlet, useLocation } from "react-router-dom";
import useUserData from "../../CustomHooks/getUserData";

const RutasProtegidas = ({ redirectTo = "/404" }) => {
    const { pathname } = useLocation();   // URL completa: /planeacion/transferencias
    const userData     = useUserData();   // decodifica JWT de sessionStorage

    // Busca si algún permiso del usuario coincide exactamente con la ruta actual
    const tieneAcceso = userData?.permisos?.some(p => p.RUTA === pathname);

    // tieneAcceso = true  → renderiza el hijo (la vista)
    // tieneAcceso = false → redirige a /404
    return tieneAcceso ? <Outlet /> : <Navigate to={redirectTo} replace />;
};

// Formato de permisos en el JWT:
// permisos: [
//   { RUTA: "/planeacion/transferencias", NOMBRE: "Transferencias", ICONO: "swap_horiz", PADRE: "Planeación", ... },
// ]`}</code></pre>
                    </SubSection>

                    <SubSection title="Cómo agregar un módulo nuevo">
                        <pre className="docs-code"><code>{`// ── PASO 1: Crear el archivo de módulo ──────────────────────────────────────
// src/Rutas/Reportes.jsx

import { Route, Routes, Navigate } from "react-router-dom";
import RutasProtegidas from "../utils/Router/RutasProtegidas";
import MiVista        from "../Views/auth/Reportes/MiVista/MiVista";

const Reportes = () => (
    <Routes>
        <Route element={<RutasProtegidas redirectTo="/404" />}>
            <Route path="mi-vista" element={<MiVista />} />
            {/* Agrega más rutas del módulo aquí */}
        </Route>
        <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
);

export default Reportes;


// ── PASO 2: Registrar el módulo en AuthApp.jsx ───────────────────────────────
import Reportes from "./Reportes";

<Routes>
    <Route path="/planeacion/*" element={<Planeacion />} />
    <Route path="/operacion/*"  element={<Operacion />}  />
    <Route path="/reportes/*"   element={<Reportes />}   />  {/* ← nuevo */}
    <Route path="*" element={<Navigate to="/404" replace />} />
</Routes>


// ── PASO 3: Asegurarse de que el usuario tenga el permiso en el JWT ──────────
// El JWT debe incluir en el array permisos:
// { RUTA: "/reportes/mi-vista", NOMBRE: "Mi Vista", ICONO: "bar_chart", PADRE: "Reportes" }
//
// La ruta coincide con la URL completa: /reportes/mi-vista
// AuthApp consume /reportes/ y Reportes.jsx ve solo: mi-vista`}</code></pre>
                    </SubSection>

                    <SubSection title="Reglas y antipatrones">
                        <div className="docs-note">
                            <span className="icon" data-icon="check_circle" style={{ color: "var(--green-600)" }} />
                            <Text size="small">
                                <strong>Correcto:</strong> paths relativos dentro del módulo (<code>path="transferencias"</code>),
                                catch-all como hermano del layout route, un único <code>&lt;Routes&gt;</code> por nivel.
                            </Text>
                        </div>
                        <div className="docs-note">
                            <span className="icon" data-icon="cancel" style={{ color: "var(--red-600)" }} />
                            <Text size="small">
                                <strong>Error: paths absolutos en módulos anidados.</strong> Si el módulo usa
                                <code> path="/planeacion/transferencias"</code>, React Router comparará ese path con el
                                sufijo restante (<code>transferencias</code>) y nunca coincidirá. El
                                catch-all <code>path="*"</code> ganará siempre y todo dará 404.
                            </Text>
                        </div>
                        <div className="docs-note">
                            <span className="icon" data-icon="cancel" style={{ color: "var(--red-600)" }} />
                            <Text size="small">
                                <strong>Error: dos <code>&lt;Routes&gt;</code> en paralelo con catch-alls.</strong> Si
                                AuthApp renderiza <code>&lt;Planeacion /&gt;</code> y <code>&lt;Operacion /&gt;</code>
                                sin un <code>&lt;Routes&gt;</code> padre que los delimite, el
                                <code> path="*"</code> del módulo que no coincide dispara un
                                <code> &lt;Navigate to="/404"&gt;</code> para TODAS las rutas del otro módulo.
                            </Text>
                        </div>
                        <div className="docs-note">
                            <span className="icon" data-icon="cancel" style={{ color: "var(--red-600)" }} />
                            <Text size="small">
                                <strong>Error: catch-all dentro del layout route.</strong> Si el
                                <code> path="*"</code> está como hijo de
                                <code> &lt;Route element={"<RutasProtegidas />"}&gt;</code>,
                                las rutas inválidas pasarán por la verificación de permisos antes de redirigir,
                                lo que puede provocar bucles o comportamientos inesperados.
                            </Text>
                        </div>
                    </SubSection>

                </Section>

            </main>
        </div>
    );
};

export default Docs;
